import React, { useState, useEffect, useContext, useCallback, useRef, memo } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from "../../components/AuthContext";
import MasterSelection from '../../components/MasterSelection';
import TileSelectionModal from '../../components/TileSelectionModal';
import StockItemSelectionModal from '../../components/StockItemSelectionModal';
import Select from 'react-select';
import {
  FaPlus,
  FaMinus,
  FaSearch,
  FaTimes,
  FaEye,
  FaEdit,
  FaBox,
  FaBoxes,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLock,
  FaInfoCircle,
  FaCube,
  FaCalculator,
  FaUser,
  FaClipboardList,
  FaBan,
  FaUserTie,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCubes,
  FaMoneyBillWave,
  FaPercentage,
  FaFileAlt,
  FaCheck,
  FaShoppingCart,
  FaPhone,
  FaMapMarkerAlt,
  FaCopy,
  FaPaste,
  FaDownload,
  FaRocket,
  FaWarehouse,
  FaArrowRight,
  FaTruck,
  FaFilter,
  FaChartBar,
  FaSave
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';


// ============================================================
// Format number utility
// ============================================================
const formatNumberWithCommas = (number) => {
  const value = typeof number === 'string' ? parseFloat(number) : number;
  if (!value || isNaN(value)) return '0.00';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value);
};

// ============================================================
// Initial form state for Sales Order
// ============================================================
const initializeFormState = (userId = '') => ({
  recipientType: 'Customer',
  recipient: null,
  date: new Date().toISOString().split('T')[0],
  items: [{
    tileId: '',
    Name: '',
    freshBoxes: 0,
    freshPieces: 0,
    damagedBoxes: 0,
    damagedPieces: 0,
    rate: 0,
    discount: 0,
    amountBeforeDiscount: 0,
    amountAfterDiscount: 0,
    tileDetails: null
  }],
  stockItems: [{
    stockItemId: '',
    name: '',
    freshQuantity: 0,
    damagedQuantity: 0,
    rate: 0,
    discount: 0,
    amountBeforeDiscount: 0,
    amountAfterDiscount: 0,
    stockItemDetails: null
  }],
  contactPersonName: '',
  contactNumber: '',
  siteAddress: '',
  salesman: [],
  dealerSalesmen: [],
  narration: '',
  totalAmount: 0,
  createdBy: userId
});

// ============================================================
// Main Component
// ============================================================
const TilesSalesOrder = () => {
  const { user } = useContext(AuthContext);

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => setDarkMode(localStorage.getItem('darkMode') === 'true'), 500);
    return () => clearInterval(interval);
  }, []);

  // Refs
  const formRef = useRef(null);
  const calculationInProgress = useRef(false);

  // Data states
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);

  // Order listing filter states
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [quickSearch, setQuickSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '', searchType: 'all', status: '', recipientType: '',
    dateRange: '', startDate: null, endDate: null,
    minAmount: '', maxAmount: '',
    salesmen: [], createdBy: []
  });
  const [filterPresets, setFilterPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tilesSO_filterPresets') || '[]'); } catch { return []; }
  });
  const [filterHistory, setFilterHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tilesSO_filterHistory') || '[]'); } catch { return []; }
  });
  const [searchSuggestionsList, setSearchSuggestionsList] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const searchInputRef = useRef(null);

  // Basic states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [editingOrderHasDeliveries, setEditingOrderHasDeliveries] = useState(false);

  // Form state
  const [form, setForm] = useState(() => initializeFormState(user?.user?._id));

  // Ensure arrays
  useEffect(() => {
    if (!form.items || !Array.isArray(form.items)) {
      setForm(prev => ({ ...prev, items: [initializeFormState().items[0]] }));
    }
    if (!form.stockItems || !Array.isArray(form.stockItems)) {
      setForm(prev => ({ ...prev, stockItems: [initializeFormState().stockItems[0]] }));
    }
  }, []);

  // Tiles selection modal states
  const [showTilesModal, setShowTilesModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  // Stock items selection modal states
  const [showStockItemsModal, setShowStockItemsModal] = useState(false);
  const [currentStockItemIndex, setCurrentStockItemIndex] = useState(0);

  // Confirmation & view modals
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRecipientModal, setShowRecipientModal] = useState(false);

  // Dealer salesmen states
  const [dealerSalesmenOptions, setDealerSalesmenOptions] = useState([]);
  const [showDealerSalesmenSection, setShowDealerSalesmenSection] = useState(false);
  const [dealerCompanyName, setDealerCompanyName] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [count, setCount] = useState(0);

  // Refs for modals
  const confirmButtonRef = useRef(null);

  // Refs for quantity inputs - stores refs for each item index (focus after tile/stock item selection)
  const quantityInputRefs = useRef({});
  const stockItemQuantityInputRefs = useRef({});

  useEffect(() => {
    if (showConfirmationModal && confirmButtonRef.current) {
      setTimeout(() => confirmButtonRef.current?.focus(), 100);
    }
  }, [showConfirmationModal]);

  // ============================================================
  // Global keyboard shortcuts
  // ============================================================
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Don't trigger if any modal is open
      if (showTilesModal || showStockItemsModal || showConfirmationModal || showViewModal || showRecipientModal) {
        return;
      }

      // Ctrl+Enter to submit form (open confirmation modal)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        const hasTiles = form.items?.some(item => item.tileId || item.Name);
        const hasStockItems = form.stockItems?.some(item => item.stockItemId || item.name);
        if (form.recipient?.value && (hasTiles || hasStockItems)) {
          handleSubmit();
        }
      }

      // Insert key to add new tile item
      if (e.key === 'Insert' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        addItem();
      }

      // Alt+T to add new tile item (alternative)
      if (e.altKey && !e.shiftKey && !e.ctrlKey && e.key.toLowerCase() === 't' && e.code === 'KeyT') {
        e.preventDefault();
        addItem();
      }

      // Shift+Insert to add new stock item
      if (e.key === 'Insert' && e.shiftKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        addStockItem();
      }

      // Alt+S to add new stock item (alternative)
      if (e.altKey && !e.shiftKey && !e.ctrlKey && e.key.toLowerCase() === 's' && e.code === 'KeyS') {
        e.preventDefault();
        addStockItem();
      }

      // Escape to cancel edit mode or close suggestions/filters
      if (e.key === 'Escape') {
        if (showSearchSuggestions) {
          e.preventDefault();
          setShowSearchSuggestions(false);
          return;
        }
        if (showFilters) {
          e.preventDefault();
          setShowFilters(false);
          return;
        }
        if (isEditing) {
          e.preventDefault();
          handleCancelEdit();
        }
      }

      // '/' or Ctrl+K to focus search
      if ((e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k')) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        const searchInput = searchInputRef.current?.querySelector('input');
        if (searchInput) searchInput.focus();
      }

      // Ctrl+F to toggle filters
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setShowFilters(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [showTilesModal, showStockItemsModal, showConfirmationModal, showViewModal, showRecipientModal, form, isEditing, showSearchSuggestions, showFilters]);

  // Safety array checks
  useEffect(() => {
    if (!Array.isArray(employees)) setEmployees([]);
    if (!Array.isArray(orders)) setOrders([]);
  }, [employees, orders]);

  // ============================================================
  // Filter orders when data changes
  // ============================================================
  useEffect(() => {
    applyOrderFilters();
  }, [orders, quickSearch, filters]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [quickSearch, filters, itemsPerPage]);

  // Generate search suggestions from loaded orders
  const generateSearchSuggestions = useCallback((searchText) => {
    if (!searchText || searchText.length < 2 || !orders?.length) {
      setSearchSuggestionsList([]);
      return;
    }
    const searchLower = searchText.toLowerCase();
    const suggestions = [];
    const seen = new Set();
    orders.forEach(order => {
      const fields = [
        { text: order.orderNumber, type: 'Order Number', fullText: `${order.orderNumber} - ${order.recipient?.name || order.recipient?.businessName || ''}` },
        { text: order.recipient?.name || order.recipient?.businessName, type: 'Recipient', fullText: `${order.recipient?.name || order.recipient?.businessName || ''} (${order.recipientType || ''})` },
        { text: order.contactPersonName, type: 'Contact', fullText: `Contact: ${order.contactPersonName}` },
        { text: order.status, type: 'Status', fullText: `Status: ${order.status}` }
      ];
      fields.forEach(f => {
        if (f.text && f.text.toLowerCase().includes(searchLower) && !seen.has(f.text)) {
          seen.add(f.text);
          suggestions.push(f);
        }
      });
    });
    setSearchSuggestionsList(suggestions.slice(0, 8));
  }, [orders]);

  // Handle quick search input change
  const handleQuickSearchChange = (value) => {
    setQuickSearch(value);
    generateSearchSuggestions(value);
    if (value.length > 1) {
      setShowSearchSuggestions(true);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSearchSuggestions(false);
    }
  };

  // Handle search keyboard nav
  const handleSearchKeyDown = (e) => {
    if (!showSearchSuggestions || searchSuggestionsList.length === 0) {
      if (e.key === 'Enter') { e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev < searchSuggestionsList.length - 1 ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : searchSuggestionsList.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && searchSuggestionsList[selectedSuggestionIndex]) {
        setQuickSearch(searchSuggestionsList[selectedSuggestionIndex].text);
        setShowSearchSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowSearchSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Filter change handler
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Date range preset handler
  const handleDateRangeChange = (rangeName) => {
    const now = new Date();
    let startDate = null, endDate = null;
    switch (rangeName) {
      case 'today': startDate = endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
      case 'yesterday': { const y = new Date(now); y.setDate(y.getDate()-1); startDate = endDate = new Date(y.getFullYear(), y.getMonth(), y.getDate()); break; }
      case 'thisWeek': { startDate = new Date(now); startDate.setDate(now.getDate()-now.getDay()); startDate.setHours(0,0,0,0); endDate = now; break; }
      case 'lastWeek': { const s = new Date(now); s.setDate(now.getDate()-now.getDay()-7); s.setHours(0,0,0,0); const e = new Date(s); e.setDate(s.getDate()+6); e.setHours(23,59,59,999); startDate = s; endDate = e; break; }
      case 'thisMonth': startDate = new Date(now.getFullYear(), now.getMonth(), 1); endDate = now; break;
      case 'lastMonth': startDate = new Date(now.getFullYear(), now.getMonth()-1, 1); endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23,59,59); break;
      case 'last7Days': { startDate = new Date(now.getTime()-7*864e5); endDate = now; break; }
      case 'last30Days': { startDate = new Date(now.getTime()-30*864e5); endDate = now; break; }
      case 'thisYear': startDate = new Date(now.getFullYear(), 0, 1); endDate = now; break;
      default: break;
    }
    setFilters(prev => ({ ...prev, dateRange: rangeName, startDate, endDate }));
  };

  // Multi-select filter handler
  const handleMultiSelectFilter = (key, selectedOptions) => {
    setFilters(prev => ({ ...prev, [key]: selectedOptions ? selectedOptions.map(o => o.value) : [] }));
  };

  // Save filter preset
  const saveFilterPreset = async () => {
    const { value: name } = await Swal.fire({ title: 'Save Filter Preset', input: 'text', inputLabel: 'Preset Name', inputPlaceholder: 'Enter a name for this preset', showCancelButton: true, inputValidator: (v) => !v && 'Please enter a name' });
    if (name) {
      const newPreset = { name, filters: { ...filters }, quickSearch, createdAt: new Date().toISOString() };
      const updated = [...filterPresets, newPreset];
      setFilterPresets(updated);
      localStorage.setItem('tilesSO_filterPresets', JSON.stringify(updated));
      Swal.fire({ icon: 'success', title: 'Preset Saved!', text: `"${name}" has been saved`, timer: 1500, showConfirmButton: false });
    }
  };

  // Load filter preset
  const loadFilterPreset = (preset) => {
    setFilters(preset.filters);
    setQuickSearch(preset.quickSearch || '');
    Swal.fire({ icon: 'success', title: 'Preset Loaded!', text: `"${preset.name}" applied`, timer: 1200, showConfirmButton: false });
  };

  // Delete filter preset
  const deleteFilterPreset = (index) => {
    const updated = filterPresets.filter((_, i) => i !== index);
    setFilterPresets(updated);
    localStorage.setItem('tilesSO_filterPresets', JSON.stringify(updated));
  };

  // Add to filter history
  const addToFilterHistory = () => {
    const hasFilters = quickSearch || Object.entries(filters).some(([k, v]) => Array.isArray(v) ? v.length > 0 : (v !== '' && v !== null));
    if (!hasFilters) return;
    const entry = { filters: { ...filters }, quickSearch, timestamp: new Date().toISOString() };
    const updated = [entry, ...filterHistory.filter((_, i) => i < 4)];
    setFilterHistory(updated);
    localStorage.setItem('tilesSO_filterHistory', JSON.stringify(updated));
  };

  // Reset all filters
  const resetAllFilters = () => {
    setQuickSearch('');
    setFilters({
      search: '', searchType: 'all', status: '', recipientType: '',
      dateRange: '', startDate: null, endDate: null,
      minAmount: '', maxAmount: '',
      salesmen: [], createdBy: []
    });
    setShowSearchSuggestions(false);
  };

  // Compute last 7 days stats from client-side orders
  const lastSevenDaysStats = React.useMemo(() => {
    if (!orders?.length) return null;
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 864e5);
    const recent = orders.filter(o => new Date(o.createdAt) >= sevenDaysAgo);
    if (!recent.length) return null;
    const pending = recent.filter(o => o.status === 'Pending');
    const partial = recent.filter(o => o.status === 'Partial');
    const fulfilled = recent.filter(o => o.status === 'Fulfilled');
    const cancelled = recent.filter(o => o.status === 'Cancelled');
    const sumAmount = (arr) => arr.reduce((s, o) => s + (parseFloat(o.totalAmount) || 0), 0);
    return {
      totalOrders: recent.length,
      pendingOrders: pending.length,
      partialOrders: partial.length,
      fulfilledOrders: fulfilled.length,
      cancelledOrders: cancelled.length,
      totalAmount: formatNumberWithCommas(sumAmount(recent)),
      pendingAmount: formatNumberWithCommas(sumAmount(pending)),
      partialAmount: formatNumberWithCommas(sumAmount(partial)),
      fulfilledAmount: formatNumberWithCommas(sumAmount(fulfilled)),
      cancelledAmount: formatNumberWithCommas(sumAmount(cancelled))
    };
  }, [orders]);

  const applyOrderFilters = () => {
    let filtered = orders || [];

    // Apply quick search
    const searchText = quickSearch.trim().toLowerCase();
    if (searchText) {
      filtered = filtered.filter(order => {
        if (filters.searchType !== 'all') {
          switch (filters.searchType) {
            case 'orderNumber': return (order.orderNumber || '').toLowerCase().includes(searchText);
            case 'recipient': return (order.recipient?.name || order.recipient?.businessName || '').toLowerCase().includes(searchText);
            case 'contact': return (order.contactPersonName || '').toLowerCase().includes(searchText);
            case 'status': return (order.status || '').toLowerCase().includes(searchText);
            default: break;
          }
        }
        const orderNumber = (order.orderNumber || '').toLowerCase();
        const recipientName = (order.recipient?.name || order.recipient?.businessName || '').toLowerCase();
        const status = (order.status || '').toLowerCase();
        const totalAmount = (order.totalAmount || '').toString();
        const contact = (order.contactPersonName || '').toLowerCase();
        return orderNumber.includes(searchText) || recipientName.includes(searchText) ||
          status.includes(searchText) || totalAmount.includes(searchText) || contact.includes(searchText);
      });
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(o => o.status === filters.status);
    }

    // Recipient type filter
    if (filters.recipientType) {
      filtered = filtered.filter(o => o.recipientType === filters.recipientType);
    }

    // Date range filter
    if (filters.startDate) {
      const start = new Date(filters.startDate); start.setHours(0,0,0,0);
      filtered = filtered.filter(o => new Date(o.createdAt) >= start);
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate); end.setHours(23,59,59,999);
      filtered = filtered.filter(o => new Date(o.createdAt) <= end);
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(o => (parseFloat(o.totalAmount) || 0) >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(o => (parseFloat(o.totalAmount) || 0) <= parseFloat(filters.maxAmount));
    }

    // Salesperson filter
    if (filters.salesmen.length > 0) {
      filtered = filtered.filter(o => {
        const orderSalesmen = Array.isArray(o.salesman) ? o.salesman.map(s => s._id || s) : [];
        return filters.salesmen.some(sid => orderSalesmen.includes(sid));
      });
    }

    // Created by filter
    if (filters.createdBy.length > 0) {
      filtered = filtered.filter(o => {
        const cid = o.createdBy?._id || o.createdBy;
        return filters.createdBy.includes(cid);
      });
    }

    setFilteredOrders(filtered);
  };

  // ============================================================
  // Select styles (matching TilesDeliveryOrder)
  // ============================================================
  const selectStyles = {
    control: (base, state) => ({
      ...base, minHeight: '44px', fontSize: '14px', borderWidth: '2px',
      borderColor: state.isFocused ? '#3b82f6' : (darkMode ? '#4b5563' : '#d1d5db'),
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      borderRadius: '10px',
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      transition: 'all 0.2s ease', cursor: 'pointer',
      '&:hover': { borderColor: darkMode ? '#60a5fa' : '#93c5fd' }
    }),
    option: (base, state) => ({
      ...base, fontSize: '14px', padding: '10px 12px', cursor: 'pointer',
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? (darkMode ? '#4b5563' : '#dbeafe') : (darkMode ? '#1f2937' : '#ffffff'),
      color: state.isSelected ? '#ffffff' : (darkMode ? '#f3f4f6' : '#1f2937'),
      transition: 'all 0.15s ease', '&:active': { backgroundColor: '#3b82f6' }
    }),
    placeholder: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '14px' }),
    singleValue: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#1f2937' }),
    multiValue: (base) => ({ ...base, backgroundColor: darkMode ? '#4b5563' : '#e0e7ff', borderRadius: '6px' }),
    multiValueLabel: (base) => ({ ...base, color: darkMode ? '#e5e7eb' : '#3730a3', fontSize: '13px', padding: '2px 6px' }),
    multiValueRemove: (base) => ({
      ...base, color: darkMode ? '#9ca3af' : '#6366f1', cursor: 'pointer',
      '&:hover': { backgroundColor: darkMode ? '#ef4444' : '#dc2626', color: '#ffffff' }
    }),
    menu: (base) => ({
      ...base, borderRadius: '10px', border: `2px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`,
      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
      boxShadow: darkMode ? '0 10px 25px -5px rgba(0,0,0,0.5)' : '0 10px 25px -5px rgba(0,0,0,0.1)',
      marginTop: '4px', zIndex: 9999
    }),
    menuList: (base) => ({ ...base, borderRadius: '8px', padding: '4px', backgroundColor: darkMode ? '#1f2937' : '#ffffff', maxHeight: '300px' }),
    dropdownIndicator: (base, state) => ({
      ...base, color: darkMode ? '#9ca3af' : '#6b7280', transition: 'all 0.2s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      '&:hover': { color: '#3b82f6' }
    }),
    clearIndicator: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280', cursor: 'pointer', '&:hover': { color: '#ef4444' } }),
    input: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#1f2937', fontSize: '14px' }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: darkMode ? '#4b5563' : '#e5e7eb' })
  };

  // ============================================================
  // fetchInitialData — employees only (recipients come from RecipientSelection)
  // ============================================================
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const employeesRes = await api.get(`/employees/company2/${user?.user?.company?._id || user?.user?.company}`).catch(() => ({ data: [] }));
      setEmployees(Array.isArray(employeesRes?.data?.employees) ? employeesRes.data.employees : []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // fetchOrders — Get sales orders list
  // ============================================================
  const fetchOrders = async () => {
    try {
      if (!user?.user?.company?._id && !user?.user?.company) return;
      setLoading(true);
      const companyId = user?.user?.company?._id || user?.user?.company;
      const response = await api.get('/tiles-sales-orders', {
        params: { page, limit, companyId }
      });
      const salesOrders = Array.isArray(response?.data?.data) ? response.data.data : [];
      setOrders(salesOrders);
      setCount(response?.data?.pagination?.totalCount || 0);
      setTotalPages(response?.data?.pagination?.totalPages || 0);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and page change
  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { fetchOrders(); }, [page]);

  // ============================================================
  // Fetch dealer salesmen when recipient is a Dealer
  // ============================================================
  useEffect(() => {
    const fetchDealerSalesmen = async () => {
      if (form.recipientType !== 'Dealer' || !form.recipient?.value) {
        setShowDealerSalesmenSection(false);
        setDealerSalesmenOptions([]);
        setDealerCompanyName('');
        return;
      }
      try {
        const companyId = user?.user?.company?._id || user?.user?.company;
        const res = await api.get(`/tiles-sales-orders/dealer-salesmen/${form.recipient.value}`, { params: { companyId } });
        if (res.data?.matched) {
          setDealerSalesmenOptions(res.data.salesmen.map(s => ({ value: s._id, label: s.name })));
          setDealerCompanyName(res.data.dealerCompanyName || '');
          setShowDealerSalesmenSection(true);
        } else {
          setShowDealerSalesmenSection(false);
          setDealerSalesmenOptions([]);
          setDealerCompanyName('');
        }
      } catch (err) {
        console.error('Error fetching dealer salesmen:', err);
        setShowDealerSalesmenSection(false);
        setDealerSalesmenOptions([]);
      }
    };
    fetchDealerSalesmen();
  }, [form.recipientType, form.recipient?.value]);

  // ============================================================
  // handleTileSelect — Select a tile from shared modal
  // ============================================================
  const handleTileSelect = (tile) => {
    if (!form.items || !Array.isArray(form.items)) return;
    const updatedItems = [...form.items];

    // Compute aggregate stock across all locations (SO has no location filter)
    // Use cornerDamagedBox/Pieces (sellable damaged only) for sales orders
    const aggStock = (Array.isArray(tile.stock) ? tile.stock : []).reduce((acc, s) => ({
      freshStockBox: acc.freshStockBox + (s.freshStockBox || 0),
      freshStockPieces: acc.freshStockPieces + (s.freshStockPieces || 0),
      cornerDamagedBox: acc.cornerDamagedBox + (s.cornerDamagedBox || 0),
      cornerDamagedPieces: acc.cornerDamagedPieces + (s.cornerDamagedPieces || 0)
    }), { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 });

    // Net Stock = (current stock + ordered + in-transit) - salesOrder
    const netStock = {
      freshBoxes: aggStock.freshStockBox + (tile.boxesOrdered || 0) + (tile.inTransitBoxes || 0) - (tile.salesOrder?.freshBoxes || 0),
      freshPieces: aggStock.freshStockPieces - (tile.salesOrder?.freshPieces || 0),
      cornerDamagedBoxes: aggStock.cornerDamagedBox - (tile.salesOrder?.cornerDamagedBoxes || 0),
      cornerDamagedPieces: aggStock.cornerDamagedPieces - (tile.salesOrder?.cornerDamagedPieces || 0)
    };

    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      tileId: tile._id,
      Name: tile.tilesName || tile.displayName || '',
      rate: 0,
      tileDetails: tile,
      netStock
    };
    setForm(prev => ({ ...prev, items: updatedItems }));
    setShowTilesModal(false);

    // Focus on first quantity input after tile selection
    setTimeout(() => {
      const itemRefs = quantityInputRefs.current[currentItemIndex];
      if (itemRefs) {
        if (itemRefs.freshBoxes) {
          itemRefs.freshBoxes.focus();
          itemRefs.freshBoxes.select();
        } else if (itemRefs.freshPieces) {
          itemRefs.freshPieces.focus();
          itemRefs.freshPieces.select();
        } else if (itemRefs.damagedBoxes) {
          itemRefs.damagedBoxes.focus();
          itemRefs.damagedBoxes.select();
        } else if (itemRefs.damagedPieces) {
          itemRefs.damagedPieces.focus();
          itemRefs.damagedPieces.select();
        }
      }
    }, 100);
  };

  // ============================================================
  // handleStockItemSelect — Select a stock item from shared modal
  // ============================================================
  const handleStockItemSelect = (stockItem) => {
    if (!form.stockItems || !Array.isArray(form.stockItems)) return;
    const newStockItems = [...form.stockItems];

    // Compute aggregate stock across all locations
    const aggStock = (Array.isArray(stockItem.stock) ? stockItem.stock : []).reduce((acc, s) => ({
      freshQuantity: acc.freshQuantity + (s.freshQuantity || 0),
      damagedQuantity: acc.damagedQuantity + (s.damagedQuantity || 0)
    }), { freshQuantity: 0, damagedQuantity: 0 });

    // Net Stock = (current stock + ordered + in-transit) - salesOrder
    const netStock = {
      freshQuantity: aggStock.freshQuantity + (stockItem.quantityOrdered || 0) + (stockItem.inTransitQuantity || 0) - (stockItem.salesOrder?.freshQuantity || 0),
      damagedQuantity: aggStock.damagedQuantity - (stockItem.salesOrder?.damagedQuantity || 0)
    };

    newStockItems[currentStockItemIndex] = {
      ...newStockItems[currentStockItemIndex],
      stockItemId: stockItem._id,
      name: stockItem.name || '',
      rate: stockItem.rate || 0,
      freshQuantity: 0,
      damagedQuantity: 0,
      discount: 0,
      amountBeforeDiscount: 0,
      amountAfterDiscount: 0,
      stockItemDetails: stockItem,
      netStock
    };
    setForm(prev => ({ ...prev, stockItems: newStockItems }));
    setShowStockItemsModal(false);

    // Focus on first quantity input after stock item selection
    setTimeout(() => {
      const itemRefs = stockItemQuantityInputRefs.current[currentStockItemIndex];
      if (itemRefs) {
        if (itemRefs.freshQuantity) {
          itemRefs.freshQuantity.focus();
          itemRefs.freshQuantity.select();
        } else if (itemRefs.damagedQuantity) {
          itemRefs.damagedQuantity.focus();
          itemRefs.damagedQuantity.select();
        }
      }
    }, 100);
  };

  // ============================================================
  // addItem / removeItem / addStockItem / removeStockItem
  // ============================================================
  const addItem = () => {
    // If there's already an empty first tile item, just open the modal for it
    const firstItem = form.items?.[0];
    if (form.items.length === 1 && !firstItem?.tileId && !firstItem?.Name) {
      setCurrentItemIndex(0);
      setShowTilesModal(true);
      return;
    }
    setForm(prev => ({
      ...prev,
      items: [...(prev.items || []), {
        tileId: '', Name: '', freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0,
        rate: 0, discount: 0, amountBeforeDiscount: 0, amountAfterDiscount: 0, tileDetails: null
      }]
    }));
    // Open the modal for the newly added item
    setTimeout(() => {
      setCurrentItemIndex(form.items.length);
      setShowTilesModal(true);
    }, 50);
  };

  const removeItem = (index) => {
    if (form.items && form.items.length > 1) {
      setForm(prev => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index) }));
      setTimeout(() => calculateTotals(), 100);
    }
  };

  const addStockItem = () => {
    // If there's already an empty first stock item, just open the modal for it
    const firstItem = form.stockItems?.[0];
    if (form.stockItems.length === 1 && !firstItem?.name && !firstItem?.stockItemId) {
      setCurrentStockItemIndex(0);
      setShowStockItemsModal(true);
      return;
    }
    setForm(prev => ({
      ...prev,
      stockItems: [...(prev.stockItems || []), {
        stockItemId: '', name: '', freshQuantity: 0, damagedQuantity: 0,
        rate: 0, discount: 0, amountBeforeDiscount: 0, amountAfterDiscount: 0, stockItemDetails: null
      }]
    }));
    // Open the modal for the newly added item
    setTimeout(() => {
      setCurrentStockItemIndex(form.stockItems.length);
      setShowStockItemsModal(true);
    }, 50);
  };

  const removeStockItem = (index) => {
    if (form.stockItems && form.stockItems.length > 1) {
      setForm(prev => ({ ...prev, stockItems: (prev.stockItems || []).filter((_, i) => i !== index) }));
      setTimeout(() => calculateTotals(), 100);
    }
  };

  // Clipboard state for copy/paste items
  const [copiedItem, setCopiedItem] = useState(() => {
    try {
      const saved = localStorage.getItem('tiles_so_clipboard_item');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [copiedStockItem, setCopiedStockItem] = useState(() => {
    try {
      const saved = localStorage.getItem('tiles_so_clipboard_stock_item');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const copyItem = (index) => {
    const itemCopy = JSON.parse(JSON.stringify(form.items[index]));
    setCopiedItem(itemCopy);
    localStorage.setItem('tiles_so_clipboard_item', JSON.stringify(itemCopy));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Item copied', showConfirmButton: false, timer: 1500 });
  };

  const pasteItem = () => {
    if (!copiedItem) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'No item copied', showConfirmButton: false, timer: 1500 });
      return;
    }
    setForm(prev => ({
      ...prev,
      items: [...(prev.items || []), JSON.parse(JSON.stringify(copiedItem))]
    }));
    setTimeout(() => calculateTotals(), 100);
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Item pasted', showConfirmButton: false, timer: 1500 });
  };

  const copyStockItem = (index) => {
    const itemCopy = JSON.parse(JSON.stringify(form.stockItems[index]));
    setCopiedStockItem(itemCopy);
    localStorage.setItem('tiles_so_clipboard_stock_item', JSON.stringify(itemCopy));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Stock item copied', showConfirmButton: false, timer: 1500 });
  };

  const pasteStockItem = () => {
    if (!copiedStockItem) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'No stock item copied', showConfirmButton: false, timer: 1500 });
      return;
    }
    setForm(prev => ({
      ...prev,
      stockItems: [...(prev.stockItems || []), JSON.parse(JSON.stringify(copiedStockItem))]
    }));
    setTimeout(() => calculateTotals(), 100);
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Stock item pasted', showConfirmButton: false, timer: 1500 });
  };

  // ============================================================
  // updateItem — Update tile item fields and recalculate
  // ============================================================
  const updateItem = (index, field, value) => {
    if (!form.items || !Array.isArray(form.items)) return;
    calculationInProgress.current = true;

    const updatedItems = [...form.items];

    // No stock validation for sales orders — it's a pre-order
    if (['freshBoxes', 'freshPieces', 'damagedBoxes', 'damagedPieces'].includes(field)) {
      updatedItems[index][field] = Math.max(0, parseInt(value) || 0);
    } else {
      updatedItems[index][field] = value;
    }

    // Recalculate amounts
    if (['freshBoxes', 'freshPieces', 'damagedBoxes', 'damagedPieces', 'rate', 'discount'].includes(field)) {
      const item = updatedItems[index];
      const totalBoxes = (item.freshBoxes || 0) + (item.damagedBoxes || 0);
      const totalPieces = (item.freshPieces || 0) + (item.damagedPieces || 0);
      const piecesPerBox = item.tileDetails?.tilesPiecesPerBox || 1;
      const totalQuantity = totalBoxes + (totalPieces / piecesPerBox);
      const rate = parseFloat(item.rate) || 0;
      const discount = parseFloat(item.discount) || 0;

      const amountBeforeDiscount = totalQuantity * rate;
      const discountAmount = (amountBeforeDiscount * discount) / 100;
      const amountAfterDiscount = amountBeforeDiscount - discountAmount;

      updatedItems[index].amountBeforeDiscount = isNaN(amountBeforeDiscount) ? 0 : amountBeforeDiscount;
      updatedItems[index].amountAfterDiscount = isNaN(amountAfterDiscount) ? 0 : amountAfterDiscount;
    }

    setForm(prev => ({ ...prev, items: updatedItems }));

    setTimeout(() => {
      calculationInProgress.current = false;
      calculateTotals();
    }, 100);
  };

  // ============================================================
  // updateStockItem — Update stock item fields and recalculate
  // ============================================================
  const updateStockItem = (index, field, value) => {
    if (!form.stockItems || !Array.isArray(form.stockItems)) return;
    calculationInProgress.current = true;

    const updated = [...form.stockItems];

    if (['freshQuantity', 'damagedQuantity'].includes(field)) {
      updated[index][field] = parseInt(value) || 0;
    } else {
      updated[index][field] = value;
    }

    if (['rate', 'discount', 'freshQuantity', 'damagedQuantity'].includes(field)) {
      const rate = parseFloat(updated[index].rate) || 0;
      const discount = parseFloat(updated[index].discount) || 0;
      const freshQty = parseInt(updated[index].freshQuantity) || 0;
      const damagedQty = parseInt(updated[index].damagedQuantity) || 0;
      const totalQuantity = freshQty + damagedQty;
      const amountBeforeDiscount = totalQuantity * rate;
      const discountAmount = (amountBeforeDiscount * discount) / 100;
      const amountAfterDiscount = amountBeforeDiscount - discountAmount;
      updated[index].amountBeforeDiscount = amountBeforeDiscount;
      updated[index].amountAfterDiscount = amountAfterDiscount;
    }

    setForm(prev => ({ ...prev, stockItems: updated }));

    setTimeout(() => {
      calculationInProgress.current = false;
      calculateTotals();
    }, 100);
  };

  // ============================================================
  // calculateTotals
  // ============================================================
  const calculateTotals = () => {
    const itemsTotal = (form.items || []).reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0);
    const stockItemsTotal = (form.stockItems || []).reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0);
    const total = itemsTotal + stockItemsTotal;

    setForm(prev => ({ ...prev, totalAmount: total }));
  };

  // ============================================================
  // getValidationReasons — Returns array of validation issues
  // ============================================================
  const getValidationReasons = () => {
    const reasons = [];
    if (!form.recipient?.value) {
      reasons.push('Recipient is required');
    }
    const hasTiles = form.items?.some(item => item.tileId || item.Name);
    const hasStockItems = form.stockItems?.some(item => item.stockItemId || item.name);
    if (!hasTiles && !hasStockItems) {
      reasons.push('At least one tile or stock item is required');
    }
    if (form.items && form.items.length > 0) {
      form.items.forEach((item, i) => {
        if (item.tileId || item.Name) {
          const totalQuantity = (item.freshBoxes || 0) + (item.freshPieces || 0) + (item.damagedBoxes || 0) + (item.damagedPieces || 0);
          if (totalQuantity === 0) reasons.push(`Tile item ${i + 1}: Quantity is required`);
          if (isNaN(item.amountAfterDiscount) || isNaN(item.rate)) reasons.push(`Tile item ${i + 1}: Invalid rate or calculation`);
        }
      });
    }
    if (form.stockItems && form.stockItems.length > 0) {
      form.stockItems.forEach((stockItem, i) => {
        if (stockItem.stockItemId || stockItem.name) {
          const totalQuantity = (stockItem.freshQuantity || 0) + (stockItem.damagedQuantity || 0);
          if (totalQuantity === 0) reasons.push(`Stock item ${i + 1}: Quantity is required`);
          if (isNaN(stockItem.amountAfterDiscount) || isNaN(stockItem.rate)) reasons.push(`Stock item ${i + 1}: Invalid rate or calculation`);
        }
      });
    }
    return reasons;
  };

  // ============================================================
  // handleRecipientSelect — Called by MasterSelection modal
  // ============================================================
  const handleRecipientSelect = (recipient, recipientType) => {
    setForm(prev => ({
      ...prev,
      recipientType: recipientType,
      recipient: {
        value: recipient._id,
        label: recipientType === 'Customer'
          ? `${recipient.name} (Ph: ${recipient.phone}, ID: ${recipient.customerCode}, Station: ${recipient.station?.name})`
          : recipient.businessName
      },
      contactPersonName: prev.contactPersonName || recipient.contactPerson || '',
      contactNumber: prev.contactNumber || recipient.phone || ''
    }));
    setShowRecipientModal(false);
  };

  // ============================================================
  // handleSubmit — Validate and show confirmation
  // ============================================================
  const handleSubmit = async () => {
    if (!form.recipient?.value) {
      Swal.fire({ icon: 'error', title: 'Recipient Required', text: 'Please select a recipient.', timer: 4000, timerProgressBar: true });
      return;
    }

    const hasTiles = form.items?.some(item => item.tileId || item.Name);
    const hasStockItems = form.stockItems?.some(item => item.stockItemId || item.name);
    if (!hasTiles && !hasStockItems) {
      Swal.fire({ icon: 'error', title: 'No Items Added', text: 'At least one tile or stock item is required.', timer: 4000, timerProgressBar: true });
      return;
    }

    // Validate tile items
    if (hasTiles) {
      for (let i = 0; i < form.items.length; i++) {
        const item = form.items[i];
        if (item.tileId || item.Name) {
          const totalQty = (item.freshBoxes || 0) + (item.freshPieces || 0) + (item.damagedBoxes || 0) + (item.damagedPieces || 0);
          if (totalQty === 0) {
            Swal.fire({ icon: 'error', title: 'Quantity Required', text: `Tile item ${i+1} needs at least one quantity value.`, timer: 4000, timerProgressBar: true });
            return;
          }
        }
      }
    }

    // Validate stock items
    if (hasStockItems) {
      for (let i = 0; i < form.stockItems.length; i++) {
        const si = form.stockItems[i];
        if (si.stockItemId || si.name) {
          const totalQty = (si.freshQuantity || 0) + (si.damagedQuantity || 0);
          if (totalQty === 0) {
            Swal.fire({ icon: 'error', title: 'Quantity Required', text: `Stock item ${i+1} needs at least one quantity value.`, timer: 4000, timerProgressBar: true });
            return;
          }
        }
      }
    }

    // Validate dealer salesmen if section is visible
    if (showDealerSalesmenSection && dealerSalesmenOptions.length > 0) {
      const selected = Array.isArray(form.dealerSalesmen) ? form.dealerSalesmen : [];
      if (selected.length === 0) {
        Swal.fire({ icon: 'error', title: 'Dealer Salesperson Required', text: 'Please select at least one dealer salesperson.', timer: 4000, timerProgressBar: true });
        return;
      }
    }

    setShowConfirmationModal(true);
  };

  // ============================================================
  // confirmSubmit — Actually submit to API
  // ============================================================
  const confirmSubmit = async () => {
    try {
      setSubmitting(true);
      setShowConfirmationModal(false);

      const sanitizedItems = (form.items || [])
        .filter(item => item.tileId || item.Name)
        .map(item => ({
          tileId: item.tileId,
          tileName: item.Name || '',
          freshBoxes: Math.max(0, parseInt(item.freshBoxes) || 0),
          freshPieces: Math.max(0, parseInt(item.freshPieces) || 0),
          damagedBoxes: Math.max(0, parseInt(item.damagedBoxes) || 0),
          damagedPieces: Math.max(0, parseInt(item.damagedPieces) || 0),
          rate: parseFloat(item.rate) || 0,
          discount: parseFloat(item.discount) || 0,
          amountBeforeDiscount: parseFloat(item.amountBeforeDiscount) || 0,
          amountAfterDiscount: parseFloat(item.amountAfterDiscount) || 0,
        }));

      const sanitizedStockItems = (form.stockItems || [])
        .filter(item => item.stockItemId && ((item.freshQuantity || 0) > 0 || (item.damagedQuantity || 0) > 0))
        .map(item => ({
          stockItemId: item.stockItemId,
          name: item.name || '',
          freshQuantity: parseInt(item.freshQuantity) || 0,
          damagedQuantity: parseInt(item.damagedQuantity) || 0,
          rate: parseFloat(item.rate) || 0,
          discount: parseFloat(item.discount) || 0,
          amountBeforeDiscount: parseFloat(item.amountBeforeDiscount) || 0,
          amountAfterDiscount: parseFloat(item.amountAfterDiscount) || 0
        }));

      const submitData = {
        date: form.date || new Date().toISOString(),
        recipientType: form.recipientType,
        recipient: form.recipient?.value,
        items: sanitizedItems,
        stockItems: sanitizedStockItems,
        contactPersonName: form.contactPersonName || '',
        contactNumber: form.contactNumber || '',
        siteAddress: form.siteAddress || '',
        salesman: Array.isArray(form.salesman) ? form.salesman.map(s => s.value || s) : [],
        dealerSalesmen: Array.isArray(form.dealerSalesmen) ? form.dealerSalesmen.map(s => s.value || s) : [],
        narration: form.narration || '',
        totalAmount: parseFloat(form.totalAmount) || 0,
        company: user?.user?.company?._id || user?.user?.company,
        createdBy: user?.user?._id
      };

      let response;
      if (isEditing && orderId) {
        response = await api.put(`/tiles-sales-orders/${orderId}`, submitData);
      } else {
        response = await api.post('/tiles-sales-orders', submitData);
      }

      Swal.fire({
        icon: 'success',
        title: `Sales Order ${isEditing ? 'Updated' : 'Created'}!`,
        html: `<p>Order: <strong>${response.data?.data?.orderNumber || 'Generated'}</strong></p>`,
        timer: 4000, timerProgressBar: true,
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f3f4f6' : '#1f2937'
      });

      if (isEditing) {
        setIsEditing(false);
        setOrderId(null);
      }
      setForm(initializeFormState(user?.user?._id));
      fetchOrders();
    } catch (error) {
      console.error('Error submitting sales order:', error);
      Swal.fire({
        icon: 'error',
        title: `Failed to ${isEditing ? 'Update' : 'Create'} Sales Order`,
        text: error.response?.data?.error || error.message || 'Unknown error',
        timer: 6000, timerProgressBar: true,
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f3f4f6' : '#1f2937'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // handleEdit — Load order into form for editing
  // ============================================================
  const handleEdit = async (order) => {
    try {
      setLoading(true);
      setIsEditing(true);

      const response = await api.get(`/tiles-sales-orders/${order._id}`);
      if (!response.data?.success || !response.data?.data) throw new Error('Invalid response');

      const od = response.data.data; // order details

      const formData = {
        ...initializeFormState(user?.user?._id),
        recipientType: od.recipientType,
        recipient: od.recipient ? {
          value: od.recipient._id,
          label: od.recipientType === 'Customer' ? od.recipient.name : (od.recipient.businessName || od.recipient.name)
        } : null,
        date: od.date ? new Date(od.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        items: (od.items || []).map(item => ({
          tileId: typeof item.tileId === 'object' ? item.tileId._id : item.tileId,
          Name: item.tileName || (typeof item.tileId === 'object' ? item.tileId.tilesName : '') || '',
          freshBoxes: parseInt(item.freshBoxes) || 0,
          freshPieces: parseInt(item.freshPieces) || 0,
          damagedBoxes: parseInt(item.damagedBoxes) || 0,
          damagedPieces: parseInt(item.damagedPieces) || 0,
          rate: parseFloat(item.rate) || 0,
          discount: parseFloat(item.discount) || 0,
          amountBeforeDiscount: parseFloat(item.amountBeforeDiscount) || 0,
          amountAfterDiscount: parseFloat(item.amountAfterDiscount) || 0,
          tileDetails: typeof item.tileId === 'object' ? item.tileId : null,
          deliveredFreshBoxes: item.deliveredFreshBoxes || 0,
          deliveredFreshPieces: item.deliveredFreshPieces || 0,
          deliveredDamagedBoxes: item.deliveredDamagedBoxes || 0,
          deliveredDamagedPieces: item.deliveredDamagedPieces || 0
        })),
        stockItems: (od.stockItems || []).length > 0 ? od.stockItems.map(si => ({
          stockItemId: typeof si.stockItemId === 'object' ? si.stockItemId._id : si.stockItemId,
          name: si.name || (typeof si.stockItemId === 'object' ? si.stockItemId.name : '') || '',
          freshQuantity: parseInt(si.freshQuantity) || 0,
          damagedQuantity: parseInt(si.damagedQuantity) || 0,
          rate: parseFloat(si.rate) || 0,
          discount: parseFloat(si.discount) || 0,
          amountBeforeDiscount: parseFloat(si.amountBeforeDiscount) || 0,
          amountAfterDiscount: parseFloat(si.amountAfterDiscount) || 0,
          stockItemDetails: typeof si.stockItemId === 'object' ? si.stockItemId : null,
          deliveredFreshQuantity: si.deliveredFreshQuantity || 0,
          deliveredDamagedQuantity: si.deliveredDamagedQuantity || 0
        })) : [initializeFormState().stockItems[0]],
        contactPersonName: od.contactPersonName || '',
        contactNumber: od.contactNumber || '',
        siteAddress: od.siteAddress || '',
        salesman: od.salesman ? od.salesman.map(s => typeof s === 'object' ? s._id : s) : [],
        dealerSalesmen: od.dealerSalesmen ? od.dealerSalesmen.map(s => typeof s === 'object' ? { value: s._id, label: s.name } : s) : [],
        narration: od.narration || od.Narration || '',
        totalAmount: parseFloat(od.totalAmount) || 0,
        createdBy: od.createdBy?._id || user?.user?._id
      };

      setForm(formData);
      setOrderId(order._id);
      setEditingOrderHasDeliveries((od.deliveryOrders?.length || 0) > 0);
      setTimeout(() => calculateTotals(), 100);
      window.scrollTo(0, 0);

      Swal.fire({ icon: 'info', title: 'Edit Mode', text: 'Order loaded for editing.', timer: 2000 });
    } catch (error) {
      console.error('Error loading order for editing:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Failed to load order details' });
      setIsEditing(false);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // handleCancelEdit — Cancel edit mode
  // ============================================================
  const handleCancelEdit = () => {
    setIsEditing(false);
    setOrderId(null);
    setEditingOrderHasDeliveries(false);
    setForm(initializeFormState(user?.user?._id));
    Swal.fire({ icon: 'info', title: 'Edit Cancelled', timer: 1500, showConfirmButton: false });
  };

  // ============================================================
  // handleViewOrder — Open view modal
  // ============================================================
  // ============================================================
  // handleCreateDO — Navigate to Delivery Order page with SO data
  // ============================================================
  const handleCreateDO = (order) => {
    // Save SO ID to localStorage for the DO page to pick up
    localStorage.setItem('pendingSOImport', JSON.stringify({
      salesOrderId: order._id,
      orderNumber: order.orderNumber,
      timestamp: Date.now()
    }));
    // Navigate to Delivery Order page
    window.location.href = '/employee/tiles-delivery-order';
  };

  const handleViewOrder = async (order) => {
    try {
      setLoading(true);
      const response = await api.get(`/tiles-sales-orders/details/${order._id}`);
      if (response.data?.success && response.data?.data) {
        setSelectedOrder(response.data.data);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch order details' });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // handleCancelOrder — Cancel a sales order
  // ============================================================
  const handleCancelOrder = async (order) => {
    const result = await Swal.fire({
      title: 'Cancel Sales Order?',
      html: `
        <div class="text-left">
          <p class="mb-3"><strong>Order:</strong> ${order.orderNumber}</p>
          <p class="text-red-500 mb-3">This will cancel the order and reverse pending tracking.</p>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Cancellation Reason:</label>
            <textarea id="cancel-reason" class="w-full p-2 border rounded" rows="2" placeholder="Enter reason..."></textarea>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, Cancel Order',
      cancelButtonText: 'Keep Order',
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      preConfirm: () => {
        const reason = document.getElementById('cancel-reason')?.value?.trim();
        if (!reason) { Swal.showValidationMessage('Please provide a reason'); return false; }
        return reason;
      }
    });

    if (result.isConfirmed && result.value) {
      try {
        await api.put(`/tiles-sales-orders/${order._id}/cancel`, {
          cancelledBy: user?.user?._id,
          reason: result.value
        });
        Swal.fire({
          icon: 'success', title: 'Order Cancelled!', text: `Order ${order.orderNumber} has been cancelled.`,
          timer: 3000, background: darkMode ? '#1f2937' : '#ffffff', color: darkMode ? '#f3f4f6' : '#1f2937'
        });
        fetchOrders();
      } catch (error) {
        Swal.fire({
          icon: 'error', title: 'Cancellation Failed',
          text: error.response?.data?.error || 'Failed to cancel order.',
          background: darkMode ? '#1f2937' : '#ffffff', color: darkMode ? '#f3f4f6' : '#1f2937'
        });
      }
    }
  };

  // ============================================================
  // Status badge helper (with dark mode support)
  // ============================================================
  const getStatusBadge = (status) => {
    const styles = {
      Pending: darkMode ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      Partial: darkMode ? 'bg-blue-900/50 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800 border border-blue-200',
      Fulfilled: darkMode ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200',
      Cancelled: darkMode ? 'bg-red-900/50 text-red-300 border border-red-700' : 'bg-red-100 text-red-800 border border-red-200'
    };
    return `px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[status] || (darkMode ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-800 border border-gray-200')}`;
  };

  // ============================================================
  // handleDuplicateOrder — Copy order to form with zeroed quantities
  // ============================================================
  const handleDuplicateOrder = (order) => {
    const duplicatedForm = {
      ...initializeFormState(user?.user?._id),
      recipientType: order.recipientType,
      recipient: order.recipient ? {
        value: order.recipient._id || order.recipient.id,
        label: order.recipient.name || order.recipient.businessName,
        ...order.recipient
      } : null,
      contactPersonName: order.contactPersonName || '',
      contactNumber: order.contactNumber || '',
      siteAddress: order.siteAddress || '',
      salesman: order.salesman || [],
      narration: order.narration || '',
      items: order.items?.map(item => ({
        tileId: typeof item.tileId === 'object' ? (item.tileId._id || item.tileId.id) : item.tileId,
        Name: item.tileName || (typeof item.tileId === 'object' ? item.tileId.tilesName : '') || item.Name || '',
        freshBoxes: 0,
        freshPieces: 0,
        damagedBoxes: 0,
        damagedPieces: 0,
        rate: item.rate || 0,
        discount: item.discount || 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        tileDetails: typeof item.tileId === 'object' ? {
          tilesPiecesPerBox: item.tileId.tilesPiecesPerBox,
          tilesSqrFtPerBox: item.tileId.tilesSqrFtPerBox,
          id: item.tileId.id
        } : item.tileDetails
      })) || [initializeFormState().items[0]],
      stockItems: order.stockItems?.map(si => ({
        stockItemId: typeof si.stockItemId === 'object' ? (si.stockItemId._id || si.stockItemId.id) : si.stockItemId,
        name: si.name || (typeof si.stockItemId === 'object' ? si.stockItemId.name : '') || '',
        freshQuantity: 0,
        damagedQuantity: 0,
        rate: si.rate || 0,
        discount: si.discount || 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        stockItemDetails: typeof si.stockItemId === 'object' ? {
          unitOfMeasure: si.stockItemId.unitOfMeasure,
          id: si.stockItemId.id
        } : si.stockItemDetails
      })) || [initializeFormState().stockItems[0]]
    };

    setForm(duplicatedForm);
    setIsEditing(false);
    setOrderId(null);

    // Scroll to form
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

    Swal.fire({
      title: 'Order Duplicated!',
      text: 'Order details copied to form. Update quantities and submit.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937'
    });
  };

  // ============================================================
  // Early return for safety
  // ============================================================
  if (!form || !form.items || !Array.isArray(form.items)) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Loading...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="w-full mx-auto p-6">

        {/* ===== HEADER ===== */}
        <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 mb-6 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaShoppingCart className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {isEditing ? 'Edit Tiles Sales Order' : 'Create Tiles Sales Order'}
                </h1>
                <p className="text-white/90">Manage tiles sales orders with stock tracking</p>
                <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/70">
                  <span className="bg-white/10 px-2 py-1 rounded">Ctrl+Enter: Submit</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Insert: Add Tile</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Shift+Insert: Add Stock Item</span>
                  {isEditing && <span className="bg-white/10 px-2 py-1 rounded">Esc: Cancel Edit</span>}
                </div>
              </div>
              {isEditing && (
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all backdrop-blur-sm flex items-center gap-2"
                >
                  <FaTimes className="w-4 h-4" />
                  Cancel Edit (Esc)
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== FORM SECTION ===== */}
        <div ref={formRef} className={`rounded-2xl shadow-xl p-8 mb-6 transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-2 border-gray-700' : 'bg-white border-2 border-gray-100'
        }`}>

          {/* === ORDER INFORMATION SECTION === */}
          <div className={`rounded-xl border-2 p-6 mb-6 transition-colors duration-300 ${
            darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-blue-100'
          }`}>
            <h3 className={`text-lg font-bold mb-5 flex items-center gap-3 ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <FaClipboardList className="w-4 h-4 text-white" />
              </div>
              Order Information
              <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>Step 1 of 3</span>
            </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Date */}
            <div className="group">
              <label className={`block text-sm font-bold mb-3 flex items-center group-hover:text-blue-600 transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <FaCalendarAlt className="w-3 h-3 text-white" />
                </div>
                Order Date
              </label>
              <input
                type="date"
                value={form.date || ''}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                className={`w-full px-4 py-2.5 border-2 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>

            {/* Recipient Type */}
            <div className="space-y-3 group">
              <label className={`block text-sm font-bold mb-3 flex items-center transition-colors duration-300 ${
                darkMode ? 'text-gray-200 group-hover:text-blue-400' : 'text-gray-800 group-hover:text-blue-700'
              }`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <FaUser className="w-3 h-3 text-white" />
                </div>
                Recipient Type <span className="text-red-500 ml-2 animate-pulse">*</span>
                {isEditing && editingOrderHasDeliveries && <FaLock className="w-3 h-3 text-gray-400 ml-2" />}
              </label>
              <Select
                value={form.recipientType ? { value: form.recipientType, label: form.recipientType } : null}
                onChange={(selectedOption) => {
                  setForm(prev => ({
                    ...prev,
                    recipientType: selectedOption?.value || '',
                    recipient: null
                  }));
                }}
                options={[
                  { value: 'Customer', label: '👤 Customer' },
                  { value: 'Dealer', label: '🏢 Dealer' }
                ]}
                placeholder={isEditing && editingOrderHasDeliveries ? "Cannot modify recipient type" : "Choose recipient type..."}
                isDisabled={isEditing && editingOrderHasDeliveries}
                styles={selectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Recipient Selection */}
            <div className="space-y-3 group">
              <label className={`block text-sm font-bold mb-3 flex items-center transition-colors duration-300 ${
                darkMode ? 'text-gray-200 group-hover:text-green-400' : 'text-gray-800 group-hover:text-green-700'
              }`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <FaUser className="w-3 h-3 text-white" />
                </div>
                Recipient <span className="text-red-500 ml-2 animate-pulse">*</span>
              </label>
              <button
                type="button"
                onClick={() => !(isEditing && editingOrderHasDeliveries) && setShowRecipientModal(true)}
                disabled={isEditing && editingOrderHasDeliveries}
                className={`w-full p-5 border-2 rounded-2xl text-left transition-all duration-300 relative overflow-hidden ${
                  isEditing && editingOrderHasDeliveries
                    ? (darkMode
                        ? 'border-gray-600 bg-gray-700 cursor-not-allowed opacity-60'
                        : 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60')
                    : (darkMode
                        ? 'border-gray-600 bg-gradient-to-r from-gray-700 via-gray-750 to-gray-700 hover:from-green-900/30 hover:via-gray-700 hover:to-blue-900/30 hover:border-green-500 focus:ring-4 focus:ring-green-500/20 focus:border-green-500 group-hover:shadow-lg'
                        : 'border-gray-200 bg-gradient-to-r from-white via-gray-50 to-white hover:from-green-50 hover:via-white hover:to-blue-50 hover:border-green-300 focus:ring-4 focus:ring-green-500/20 focus:border-green-500 group-hover:shadow-lg')
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${form.recipient ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {isEditing && editingOrderHasDeliveries ? (
                        <FaLock className="w-4 h-4 text-gray-400" />
                      ) : form.recipient ? (
                        <FaCheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <FaUser className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    <span className={`font-semibold transition-colors duration-300 ${
                      form.recipient
                        ? (darkMode ? 'text-gray-100' : 'text-gray-900')
                        : (darkMode ? 'text-gray-400' : 'text-gray-500')
                    }`}>
                      {isEditing && editingOrderHasDeliveries
                        ? (form.recipient ? `${form.recipient.label} (Locked)` : "Cannot modify recipient")
                        : (form.recipient
                            ? form.recipient.label
                            : `Select ${form.recipientType || 'Recipient'}`
                          )}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {form.recipient && (
                      <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
                        darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                      }`}>
                        Selected
                      </div>
                    )}
                    <svg className={`w-6 h-6 transition-colors ${
                      darkMode ? 'text-gray-400 group-hover:text-blue-400' : 'text-gray-400 group-hover:text-blue-500'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          </div>
          </div>

          {/* === CONTACT & SITE DETAILS SECTION === */}
          <div className={`rounded-xl border-2 p-6 mb-6 transition-colors duration-300 ${
            darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-r from-green-50/50 to-emerald-50/50 border-green-100'
          }`}>
            <h3 className={`text-lg font-bold mb-5 flex items-center gap-3 ${darkMode ? 'text-green-400' : 'text-green-800'}`}>
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <FaPhone className="w-4 h-4 text-white" />
              </div>
              Contact & Site Details
            </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group">
              <label className={`block text-sm font-bold mb-3 flex items-center group-hover:text-green-600 transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <FaUser className="w-3 h-3 text-white" />
                </div>
                Contact Person Name
              </label>
              <input
                type="text"
                value={form.contactPersonName || ''}
                onChange={(e) => setForm(prev => ({ ...prev, contactPersonName: e.target.value }))}
                placeholder="Enter contact person name"
                className={`w-full px-4 py-2.5 border-2 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>
            <div className="group">
              <label className={`block text-sm font-bold mb-3 flex items-center group-hover:text-green-600 transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <FaPhone className="w-3 h-3 text-white" />
                </div>
                Contact Number
              </label>
              <input
                type="text"
                value={form.contactNumber || ''}
                onChange={(e) => setForm(prev => ({ ...prev, contactNumber: e.target.value }))}
                placeholder="Enter contact number"
                className={`w-full px-4 py-2.5 border-2 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>
            <div className="group">
              <label className={`block text-sm font-bold mb-3 flex items-center group-hover:text-green-600 transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <FaMapMarkerAlt className="w-3 h-3 text-white" />
                </div>
                Site Address
              </label>
              <input
                type="text"
                value={form.siteAddress || ''}
                onChange={(e) => setForm(prev => ({ ...prev, siteAddress: e.target.value }))}
                placeholder="Enter site address"
                className={`w-full px-4 py-2.5 border-2 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>
          </div>
          </div>

          {/* === SALESMAN & NARRATION SECTION === */}
          <div className={`rounded-xl border-2 p-6 mb-6 transition-colors duration-300 ${
            darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-r from-purple-50/50 to-violet-50/50 border-purple-100'
          }`}>
            <h3 className={`text-lg font-bold mb-5 flex items-center gap-3 ${darkMode ? 'text-purple-400' : 'text-purple-800'}`}>
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                <FaFileAlt className="w-4 h-4 text-white" />
              </div>
              Additional Details
            </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group">
              <label className={`block text-sm font-bold mb-3 flex items-center group-hover:text-purple-600 transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <FaUserTie className="w-3 h-3 text-white" />
                </div>
                Salesperson(s)
              </label>
              <Select
                isMulti
                styles={selectStyles}
                value={
                  Array.isArray(form.salesman)
                    ? form.salesman.map(s => {
                        if (typeof s === 'object' && s.value) return s;
                        const emp = employees.find(e => e._id === s);
                        return emp ? { value: emp._id, label: emp.name } : { value: s, label: s };
                      })
                    : []
                }
                onChange={(selected) => setForm(prev => ({ ...prev, salesman: selected || [] }))}
                options={employees.map(e => ({ value: e._id, label: e.name }))}
                placeholder="Select salesperson(s)..."
                isClearable
              />
            </div>
            <div className="group">
              <label className={`block text-sm font-bold mb-3 flex items-center group-hover:text-purple-600 transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                  <FaFileAlt className="w-3 h-3 text-white" />
                </div>
                Narration
              </label>
              <textarea
                value={form.narration || ''}
                onChange={(e) => setForm(prev => ({ ...prev, narration: e.target.value }))}
                placeholder="Enter narration / remarks..."
                rows={2}
                className={`w-full px-4 py-2.5 border-2 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>
          </div>

          {/* Dealer Salesperson(s) - shown only when dealer matches firm's dealerReferences */}
          {showDealerSalesmenSection && dealerSalesmenOptions.length > 0 && (
            <div className="mt-4">
              <label className={`block text-sm font-bold mb-3 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="w-6 h-6 mr-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <FaUserTie className="w-3 h-3 text-white" />
                </div>
                Dealer Salesperson(s) <span className="text-red-500 ml-1 animate-pulse">*</span>
                {dealerCompanyName && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                    {dealerCompanyName}
                  </span>
                )}
              </label>
              <Select
                isMulti
                styles={selectStyles}
                value={
                  Array.isArray(form.dealerSalesmen)
                    ? form.dealerSalesmen.map(s => {
                        if (typeof s === 'object' && s.value) return s;
                        const opt = dealerSalesmenOptions.find(o => o.value === s);
                        return opt || { value: s, label: s };
                      })
                    : []
                }
                onChange={(selected) => setForm(prev => ({ ...prev, dealerSalesmen: selected || [] }))}
                options={dealerSalesmenOptions}
                placeholder="Select dealer salesperson(s)..."
                isClearable
              />
            </div>
          )}

          </div>

          {/* ===== TILES ITEMS SECTION ===== */}
          <div className={`rounded-xl border-2 p-6 mb-6 transition-colors duration-300 ${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-blue-200'}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-lg font-bold flex items-center gap-3 ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <FaCubes className="w-4 h-4 text-white" />
                </div>
                Tiles Items
                <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>Step 2 of 3</span>
              </h3>
              <button onClick={addItem} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl text-sm flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105">
                <FaPlus className="w-3 h-3" /> Add Tile
              </button>
              <button onClick={pasteItem} disabled={!copiedItem} className={`px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-all duration-200 border disabled:opacity-40 ${darkMode ? 'border-teal-700 text-teal-300 hover:bg-teal-900/30' : 'border-teal-300 text-teal-700 hover:bg-teal-50'}`}>
                <FaPaste className="w-3 h-3" /> Paste Tile
              </button>
            </div>

            {form.items.map((item, index) => (
              <div key={index} className={`rounded-xl p-6 mb-4 transition-all duration-300 border-2 shadow-sm hover:shadow-lg ${
                darkMode ? 'bg-gray-800 border-gray-600 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-white'
              }`}>
                <div className={`flex items-center justify-between pb-3 mb-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Item {index + 1}
                    </span>
                    {item.Name && (
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-green-100 text-green-700 border border-green-200'}`}>
                        ✓ Selected
                      </div>
                    )}
                  </div>
                  {form.items.length > 1 && (
                    <button onClick={() => removeItem(index)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`} title="Remove item">
                      <FaMinus className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => copyItem(index)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-violet-400 hover:bg-violet-900/30' : 'text-violet-600 hover:bg-violet-100'}`} title="Copy item">
                    <FaCopy className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Tile Selection Button */}
                <button
                  onClick={() => { setCurrentItemIndex(index); setShowTilesModal(true); }}
                  className={`w-full mb-4 px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                    item.Name
                      ? (darkMode ? 'border-green-600 bg-green-900/20 text-green-400 hover:bg-green-900/30' : 'border-green-400 bg-green-50 text-green-700 hover:bg-green-100')
                      : (darkMode ? 'border-gray-500 text-gray-400 hover:border-blue-500 hover:text-blue-400' : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600')
                  }`}
                >
                  {item.Name ? (
                    <>
                      <FaCheckCircle className="w-4 h-4" />
                      <span className="font-semibold">{item.Name}</span>
                      {item.tileDetails?.tilesPiecesPerBox && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-green-100 text-green-600'}`}>
                          {item.tileDetails.tilesPiecesPerBox} pcs/box
                        </span>
                      )}
                      <span className={`ml-auto text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Click to change</span>
                    </>
                  ) : (
                    <>
                      <FaSearch className="w-4 h-4" />
                      <span>Click to search and select a tile</span>
                    </>
                  )}
                </button>

                {/* Net Stock Display */}
                {item.netStock && item.tileId && item.Name && (
                  <div className={`mb-3 border rounded-lg p-3 transition-colors duration-300 ${
                    darkMode ? 'bg-cyan-900/20 border-cyan-700' : 'bg-cyan-50 border-cyan-200'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <FaWarehouse className="w-4 h-4 text-cyan-600" />
                      <h4 className={`text-sm font-bold ${darkMode ? 'text-cyan-300' : 'text-cyan-900'}`}>Net Stock</h4>
                      <span className={`text-xs ${darkMode ? 'text-cyan-400/60' : 'text-cyan-600/60'}`}>(Stock + Ordered + Transit - Sales Orders)</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className={`rounded p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Fresh Box</span>
                        <div className={`font-bold ${(item.netStock.freshBoxes || 0) < 0 ? 'text-red-500' : (darkMode ? 'text-green-300' : 'text-green-700')}`}>
                          {item.netStock.freshBoxes || 0}
                        </div>
                      </div>
                      <div className={`rounded p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Fresh Pcs</span>
                        <div className={`font-bold ${(item.netStock.freshPieces || 0) < 0 ? 'text-red-500' : (darkMode ? 'text-blue-300' : 'text-blue-700')}`}>
                          {item.netStock.freshPieces || 0}
                        </div>
                      </div>
                      <div className={`rounded p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Corner Dmg Box</span>
                        <div className={`font-bold ${(item.netStock.cornerDamagedBoxes || 0) < 0 ? 'text-red-500' : (darkMode ? 'text-yellow-300' : 'text-yellow-700')}`}>
                          {item.netStock.cornerDamagedBoxes || 0}
                        </div>
                      </div>
                      <div className={`rounded p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Corner Dmg Pcs</span>
                        <div className={`font-bold ${(item.netStock.cornerDamagedPieces || 0) < 0 ? 'text-red-500' : (darkMode ? 'text-yellow-300' : 'text-yellow-700')}`}>
                          {item.netStock.cornerDamagedPieces || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quantities */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  {[
                    { label: 'Fresh Boxes', field: 'freshBoxes', icon: FaBox, color: 'green' },
                    { label: 'Fresh Pieces', field: 'freshPieces', icon: FaCube, color: 'blue' },
                    { label: 'Corner Dmg Boxes', field: 'damagedBoxes', icon: FaBox, color: 'yellow' },
                    { label: 'Corner Dmg Pieces', field: 'damagedPieces', icon: FaCube, color: 'yellow' }
                  ].map(({ label, field, icon: Icon, color }) => (
                    <div key={field}>
                      <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <Icon className="inline mr-1 w-3 h-3" /> {label}
                      </label>
                      <input
                        ref={(el) => {
                          if (!quantityInputRefs.current[index]) quantityInputRefs.current[index] = {};
                          quantityInputRefs.current[index][field] = el;
                        }}
                        type="number" min="0"
                        value={item[field] || 0}
                        onChange={(e) => updateItem(index, field, e.target.value)}
                        className={`w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-300 ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500`}
                      />
                    </div>
                  ))}
                </div>

                {/* Rate, Discount, Amount */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaMoneyBillWave className="inline mr-1 w-3 h-3" /> Rate
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      value={item.rate || ''}
                      onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaPercentage className="inline mr-1 w-3 h-3" /> Discount %
                    </label>
                    <input
                      type="number" min="0" max="100" step="0.01"
                      value={item.discount || ''}
                      onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaCalculator className="inline mr-1 w-3 h-3" /> Amount
                    </label>
                    <div className={`px-3 py-2 border-2 rounded-lg text-sm font-bold ${
                      darkMode ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-700 text-green-400' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 text-green-700'
                    }`}>
                      ₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}
                      {item.discount > 0 && item.amountBeforeDiscount > 0 && (
                        <span className={`block text-xs font-normal line-through ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          ₹{formatNumberWithCommas(item.amountBeforeDiscount)} ({item.discount}% off)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ===== STOCK ITEMS SECTION ===== */}
          <div className={`rounded-xl border-2 p-6 mb-6 transition-colors duration-300 shadow-md hover:shadow-lg ${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-r from-purple-50/50 to-violet-50/50 border-purple-200'}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-lg font-bold flex items-center gap-3 ${darkMode ? 'text-purple-400' : 'text-purple-800'}`}>
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                  <FaBoxes className="w-4 h-4 text-white" />
                </div>
                Stock Items
                <span className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>Optional</span>
              </h3>
              <button onClick={addStockItem} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white rounded-xl text-sm flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105">
                <FaPlus className="w-3 h-3" /> Add Stock Item
              </button>
              <button onClick={pasteStockItem} disabled={!copiedStockItem} className={`px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-all duration-200 border disabled:opacity-40 ${darkMode ? 'border-teal-700 text-teal-300 hover:bg-teal-900/30' : 'border-teal-300 text-teal-700 hover:bg-teal-50'}`}>
                <FaPaste className="w-3 h-3" /> Paste Stock Item
              </button>
            </div>

            {/* Empty State */}
            {form.stockItems.length === 1 && !form.stockItems[0].name && !form.stockItems[0].stockItemId ? (
              <div 
                onClick={() => { setCurrentStockItemIndex(0); setShowStockItemsModal(true); }}
                className={`text-center py-6 mb-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors duration-300 ${darkMode ? 'border-gray-600 text-gray-500 hover:border-purple-500 hover:text-purple-400' : 'border-gray-300 text-gray-400 hover:border-purple-400 hover:text-purple-600'}`}
              >
                <FaBoxes className={`w-8 h-8 mx-auto mb-2 opacity-30`} />
                <p className="text-sm">No stock items added yet. Click here or "Add Stock Item" to get started.</p>
              </div>
            ) : null}

            {form.stockItems.map((stockItem, index) => {
              // Skip rendering the initial empty placeholder when empty state is shown
              if (form.stockItems.length === 1 && !stockItem.name && !stockItem.stockItemId) return null;
              return (
              <div key={index} className={`rounded-xl p-6 mb-4 transition-all duration-300 border-2 shadow-sm hover:shadow-lg ${
                darkMode ? 'bg-gray-800 border-gray-600 hover:border-purple-500' : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-gradient-to-r hover:from-purple-50/30 hover:to-white'
              }`}>
                <div className={`flex items-center justify-between pb-3 mb-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Stock Item {index + 1}
                    </span>
                    {stockItem.name && (
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-900/50 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-700 border border-purple-200'}`}>
                        ✓ Selected
                      </div>
                    )}
                  </div>
                  {form.stockItems.length > 1 && (
                    <button onClick={() => removeStockItem(index)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`} title="Remove item">
                      <FaMinus className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => copyStockItem(index)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-violet-400 hover:bg-violet-900/30' : 'text-violet-600 hover:bg-violet-100'}`} title="Copy stock item">
                    <FaCopy className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Stock Item Selection Button */}
                <button
                  onClick={() => { setCurrentStockItemIndex(index); setShowStockItemsModal(true); }}
                  className={`w-full mb-4 px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                    stockItem.name
                      ? (darkMode ? 'border-purple-600 bg-purple-900/20 text-purple-400 hover:bg-purple-900/30' : 'border-purple-400 bg-purple-50 text-purple-700 hover:bg-purple-100')
                      : (darkMode ? 'border-gray-500 text-gray-400 hover:border-purple-500 hover:text-purple-400' : 'border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600')
                  }`}
                >
                  {stockItem.name ? (
                    <>
                      <FaCheckCircle className="w-4 h-4" />
                      <span className="font-semibold">{stockItem.name}</span>
                      <span className={`ml-auto text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Click to change</span>
                    </>
                  ) : (
                    <>
                      <FaSearch className="w-4 h-4" />
                      <span>Click to search and select a stock item</span>
                    </>
                  )}
                </button>

                {/* Net Stock Display */}
                {stockItem.netStock && stockItem.stockItemId && stockItem.name && (
                  <div className={`mb-3 border rounded-lg p-3 transition-colors duration-300 ${
                    darkMode ? 'bg-cyan-900/20 border-cyan-700' : 'bg-cyan-50 border-cyan-200'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <FaWarehouse className="w-4 h-4 text-cyan-600" />
                      <h4 className={`text-sm font-bold ${darkMode ? 'text-cyan-300' : 'text-cyan-900'}`}>Net Stock</h4>
                      <span className={`text-xs ${darkMode ? 'text-cyan-400/60' : 'text-cyan-600/60'}`}>(Stock + Ordered + Transit - Sales Orders)</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={`rounded p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Fresh Qty</span>
                        <div className={`font-bold ${(stockItem.netStock.freshQuantity || 0) < 0 ? 'text-red-500' : (darkMode ? 'text-green-300' : 'text-green-700')}`}>
                          {stockItem.netStock.freshQuantity || 0}
                        </div>
                      </div>
                      <div className={`rounded p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Damaged Qty</span>
                        <div className={`font-bold ${(stockItem.netStock.damagedQuantity || 0) < 0 ? 'text-red-500' : (darkMode ? 'text-orange-300' : 'text-orange-700')}`}>
                          {stockItem.netStock.damagedQuantity || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Fresh Qty</label>
                    <input
                      ref={(el) => {
                        if (!stockItemQuantityInputRefs.current[index]) stockItemQuantityInputRefs.current[index] = {};
                        stockItemQuantityInputRefs.current[index].freshQuantity = el;
                      }}
                      type="number" min="0"
                      value={stockItem.freshQuantity || 0}
                      onChange={(e) => updateStockItem(index, 'freshQuantity', e.target.value)}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Damaged Qty</label>
                    <input
                      ref={(el) => {
                        if (!stockItemQuantityInputRefs.current[index]) stockItemQuantityInputRefs.current[index] = {};
                        stockItemQuantityInputRefs.current[index].damagedQuantity = el;
                      }}
                      type="number" min="0"
                      value={stockItem.damagedQuantity || 0}
                      onChange={(e) => updateStockItem(index, 'damagedQuantity', e.target.value)}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-red-500 focus:border-red-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaMoneyBillWave className="inline mr-1 w-3 h-3" /> Rate
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      value={stockItem.rate || ''}
                      onChange={(e) => updateStockItem(index, 'rate', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaPercentage className="inline mr-1 w-3 h-3" /> Discount %
                    </label>
                    <input
                      type="number" min="0" max="100" step="0.01"
                      value={stockItem.discount || ''}
                      onChange={(e) => updateStockItem(index, 'discount', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                    />
                  </div>
                </div>

                {/* Amount Display */}
                <div className={`mt-3 px-4 py-2.5 rounded-lg text-right font-bold ${
                  darkMode ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-700 text-green-400' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700'
                }`}>
                  Amount: ₹{formatNumberWithCommas(stockItem.amountAfterDiscount || 0)}
                  {stockItem.discount > 0 && stockItem.amountBeforeDiscount > 0 && (
                    <span className={`ml-2 text-xs font-normal line-through ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      ₹{formatNumberWithCommas(stockItem.amountBeforeDiscount)} ({stockItem.discount}% off)
                    </span>
                  )}
                </div>
              </div>
            );
            })}
          </div>

          {/* ===== READY TO LAUNCH - SUBMIT SECTION ===== */}
          <div className={`relative rounded-2xl border-2 p-8 overflow-hidden transition-all duration-300 ${
            darkMode
              ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-600'
              : 'bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 border-green-200'
          }`}>
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl ${darkMode ? 'bg-green-900/20' : 'bg-green-200/30'}`}></div>
              <div className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-3xl ${darkMode ? 'bg-blue-900/20' : 'bg-blue-200/30'}`}></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <FaRocket className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <h3 className={`text-2xl font-black bg-gradient-to-r bg-clip-text text-transparent ${
                      darkMode ? 'from-gray-100 via-blue-300 to-purple-300' : 'from-gray-900 via-blue-900 to-purple-900'
                    }`}>Ready to Launch!</h3>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Step 3 of 3 - Review and create your order</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex space-x-1">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }}></div>
                        ))}
                      </div>
                      <span className="text-xs text-green-600 font-bold">All Steps Complete</span>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  {/* Reset / Cancel Button */}
                  <button
                    type="button"
                    onClick={isEditing ? handleCancelEdit : () => setForm(prev => ({
                      ...prev,
                      recipient: null,
                      recipientType: 'Customer',
                      items: [{ tileId: '', Name: '', freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0, rate: 0, discount: 0, amountBeforeDiscount: 0, amountAfterDiscount: 0, availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 }, tileDetails: null }],
                      stockItems: [{ stockItemId: '', name: '', freshQuantity: 0, damagedQuantity: 0, rate: 0, discount: 0, amountBeforeDiscount: 0, amountAfterDiscount: 0, availableStock: { freshQuantity: 0, damagedQuantity: 0 }, stockItemDetails: null }],
                      totalAmount: 0
                    }))}
                    className={`group/reset relative px-6 py-4 border-2 rounded-2xl transition-all duration-300 flex items-center space-x-3 font-bold shadow-md hover:shadow-lg overflow-hidden ${
                      darkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600 hover:border-gray-500'
                        : 'border-gray-300 text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:border-gray-400'
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 opacity-0 group-hover/reset:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10 flex items-center space-x-2">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-red-500 to-orange-600 flex items-center justify-center">
                        <FaTimes className="w-3 h-3 text-white" />
                      </div>
                      <span>{isEditing ? 'Cancel Edit' : 'Reset Form'}</span>
                    </div>
                  </button>

                  {/* Submit Button */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !form.recipient?.value}
                    className="group/submit relative px-10 py-4 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 text-white rounded-2xl hover:from-green-600 hover:via-emerald-600 hover:to-teal-700 disabled:from-gray-400 disabled:via-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed flex items-center space-x-3 shadow-xl hover:shadow-2xl transition-all duration-300 font-black text-lg overflow-hidden transform hover:scale-105"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 -translate-x-full group-hover/submit:translate-x-full transition-transform duration-1000"></div>
                    <div className="relative z-10 flex items-center space-x-3">
                      {submitting ? (
                        <div className="relative">
                          <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent"></div>
                          <div className="absolute inset-0 animate-ping rounded-full h-6 w-6 border border-white opacity-20"></div>
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover/submit:rotate-12 transition-transform duration-300">
                          <FaRocket className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <span>{submitting ? (isEditing ? 'Updating Order...' : 'Creating Order...') : (isEditing ? 'Update Order' : 'Create Order')}</span>
                      {!submitting && (
                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover/submit:translate-x-1 transition-transform duration-300">
                          <FaArrowRight className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    {!submitting && (
                      <div className="absolute inset-0 overflow-hidden rounded-2xl">
                        <div className="absolute top-2 left-4 w-1 h-1 bg-white rounded-full animate-bounce opacity-60" style={{ animationDelay: '0s' }}></div>
                        <div className="absolute top-4 right-6 w-1 h-1 bg-white rounded-full animate-bounce opacity-40" style={{ animationDelay: '0.5s' }}></div>
                        <div className="absolute bottom-3 left-8 w-1 h-1 bg-white rounded-full animate-bounce opacity-50" style={{ animationDelay: '1s' }}></div>
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Validation Reasons Display */}
              {(() => {
                const validationReasons = getValidationReasons();
                if (validationReasons.length > 0) {
                  return (
                    <div className={`mt-4 p-4 rounded-lg border transition-colors duration-300 ${
                      darkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start space-x-2">
                        <FaExclamationTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
                        <div className="flex-1">
                          <h4 className={`font-semibold text-sm mb-2 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                            Cannot {isEditing ? 'Update' : 'Create'} Order - Please fix the following:
                          </h4>
                          <ul className={`text-sm space-y-1 ${darkMode ? 'text-red-200' : 'text-red-600'}`}>
                            {validationReasons.map((reason, idx) => (
                              <li key={idx} className="flex items-center space-x-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"></span>
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Progress Indicator */}
              {(() => {
                const validationReasons = getValidationReasons();
                const totalChecks = 3;
                const passedChecks = totalChecks - Math.min(validationReasons.length, totalChecks);
                const completionPercent = Math.round((passedChecks / totalChecks) * 100);
                return (
                  <div className={`mt-6 pt-4 border-t ${darkMode ? 'border-gray-700/50' : 'border-gray-200/50'}`}>
                    <div className={`flex items-center justify-between text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>Form Completion</span>
                      <span className={`font-bold ${completionPercent === 100 ? 'text-green-600' : completionPercent >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{completionPercent}%</span>
                    </div>
                    <div className={`w-full rounded-full h-2 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className={`h-full rounded-full transition-all duration-500 shadow-sm ${
                          completionPercent === 100
                            ? 'bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600'
                            : completionPercent >= 50
                              ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                              : 'bg-gradient-to-r from-red-500 to-pink-500'
                        }`}
                        style={{ width: `${completionPercent}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Amount Summary */}
            <div className={`relative z-10 mt-6 rounded-lg shadow-lg overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Amount Summary</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    {/* Tiles Summary */}
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <h4 className={`font-semibold mb-3 text-base ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Tiles Summary</h4>
                      {form.items.filter(item => item.Name || item.tileId).map((item, index) => (
                        <div key={index} className="flex justify-between text-sm mb-2">
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{item.Name || `Tile #${index + 1}`}</span>
                          <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}</span>
                        </div>
                      ))}
                      <div className={`border-t mt-2 pt-2 flex justify-between font-semibold ${darkMode ? 'border-gray-600 text-gray-100' : 'border-gray-300 text-gray-800'}`}>
                        <span>Tiles Total:</span>
                        <span>₹{formatNumberWithCommas(form.items.reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0))}</span>
                      </div>
                    </div>

                    {/* Stock Items Summary */}
                    {form.stockItems?.some(item => item.name || item.stockItemId) && (
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                        <h4 className={`font-semibold mb-3 text-base ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>Stock Items Summary</h4>
                        {form.stockItems.filter(item => item.name || item.stockItemId).map((stockItem, index) => (
                          <div key={index} className="flex justify-between text-sm mb-2">
                            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{stockItem.name || `Stock Item #${index + 1}`}</span>
                            <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>₹{formatNumberWithCommas(stockItem.amountAfterDiscount || 0)}</span>
                          </div>
                        ))}
                        <div className={`border-t mt-2 pt-2 flex justify-between font-semibold ${darkMode ? 'border-purple-700 text-purple-300' : 'border-purple-300 text-purple-800'}`}>
                          <span>Stock Items Total:</span>
                          <span>₹{formatNumberWithCommas(form.stockItems.reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0))}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col justify-end">
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="flex justify-between items-center mb-4">
                        <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Total Amount:</span>
                        <span className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{formatNumberWithCommas(form.totalAmount || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== ORDERS LIST SECTION ===== */}
        <div className={`rounded-2xl shadow-xl overflow-hidden transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-2 border-gray-700' : 'bg-white border-2 border-gray-100'
        }`}>
          {/* Gradient Header Banner */}
          <div className={`p-6 ${darkMode ? 'bg-gradient-to-r from-gray-800 via-gray-750 to-gray-800' : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                  <FaClipboardList className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Sales Orders</h2>
                  <p className="text-indigo-100 text-sm">{count} total orders</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const csvContent = [
                      ['Order Number', 'Recipient', 'Recipient Type', 'Status', 'Total Amount', 'Date', 'Contact Person'],
                      ...filteredOrders.map(order => [
                        order.orderNumber || '',
                        order.recipient?.name || order.recipient?.businessName || '',
                        order.recipientType || '',
                        order.status || '',
                        (order.totalAmount || 0).toString(),
                        ((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(order.createdAt)),
                        order.contactPersonName || ''
                      ])
                    ].map(row => row.join(',')).join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `sales-orders-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  }}
                  className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl text-white hover:bg-white/30 text-sm flex items-center gap-2 transition-all duration-200"
                  title="Export filtered orders to CSV"
                >
                  <FaDownload className="w-3 h-3" /> Export
                </button>
                <button onClick={fetchOrders} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl text-white hover:bg-white/30 text-sm flex items-center gap-2 transition-all duration-200">
                  <FaSearch className="w-3 h-3" /> Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">

          {/* Controls Row: Show/Hide Filters + Items Per Page */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                showFilters
                  ? darkMode ? 'bg-indigo-900/40 text-indigo-300 border border-indigo-600' : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : darkMode ? 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              <FaFilter className="w-3 h-3" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {(() => {
                const activeCount = [
                  filters.status, filters.recipientType,
                  filters.startDate, filters.endDate,
                  filters.minAmount, filters.maxAmount,
                  filters.salesmen.length > 0, filters.createdBy.length > 0
                ].filter(Boolean).length;
                return activeCount > 0 ? (
                  <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${darkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-600 text-white'}`}>{activeCount}</span>
                ) : null;
              })()}
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Items per page:</span>
              <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className={`px-2 py-1 text-xs rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-200 text-gray-700'}`}
              >
                {[10, 15, 20, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {/* Always-Visible Quick Search */}
          <div className="mb-4 relative" ref={searchInputRef}>
            <div className="relative">
              <FaSearch className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                value={quickSearch}
                onChange={(e) => handleQuickSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => { if (quickSearch.length > 1 && searchSuggestionsList.length > 0) setShowSearchSuggestions(true); }}
                placeholder="Quick search: order #, recipient, contact, status... (Press / to focus)"
                className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl text-sm transition-all duration-300 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-indigo-500'
                } focus:ring-2 focus:ring-indigo-500/20`}
              />
              {quickSearch && (
                <button onClick={() => { setQuickSearch(''); setShowSearchSuggestions(false); }} className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-400'}`}>
                  <FaTimes className="w-3 h-3" />
                </button>
              )}
            </div>
            {/* Search Suggestions Dropdown */}
            {showSearchSuggestions && searchSuggestionsList.length > 0 && (
              <div className={`absolute z-50 w-full mt-1 rounded-xl border-2 shadow-xl max-h-64 overflow-y-auto ${
                darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
              }`}>
                {searchSuggestionsList.map((s, idx) => (
                  <button key={idx} onClick={() => { setQuickSearch(s.text); setShowSearchSuggestions(false); }}
                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between transition-colors ${
                      idx === selectedSuggestionIndex
                        ? darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'
                        : darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                    } ${idx > 0 ? (darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100') : ''}`}
                  >
                    <span>{s.fullText || s.text}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{s.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {[
              { label: 'Pending', status: 'Pending', color: 'yellow' },
              { label: 'Partial', status: 'Partial', color: 'blue' },
              { label: 'Fulfilled', status: 'Fulfilled', color: 'green' }
            ].map(btn => (
              <button key={btn.status} onClick={() => handleFilterChange('status', filters.status === btn.status ? '' : btn.status)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                  filters.status === btn.status
                    ? `bg-${btn.color}-500 text-white shadow-md`
                    : darkMode ? `bg-gray-700 text-gray-300 hover:bg-gray-600` : `bg-gray-100 text-gray-600 hover:bg-gray-200`
                }`}
              >{btn.label}</button>
            ))}
            <button onClick={resetAllFilters}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                darkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-50 text-red-600 hover:bg-red-100'
              }`}
            >Clear All</button>
            <div className="relative group ml-auto">
              <button className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}>
                <FaInfoCircle className="w-3.5 h-3.5" />
              </button>
              <div className={`absolute right-0 top-full mt-1 w-52 p-3 rounded-xl shadow-xl border-2 text-xs z-50 hidden group-hover:block ${
                darkMode ? 'bg-gray-800 border-gray-600 text-gray-300' : 'bg-white border-gray-200 text-gray-600'
              }`}>
                <p className="font-bold mb-1">Keyboard Shortcuts</p>
                <p><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">/</kbd> or <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">Ctrl+K</kbd> — Focus search</p>
                <p><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">Ctrl+F</kbd> — Toggle filters</p>
                <p><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">Esc</kbd> — Close panels</p>
              </div>
            </div>
          </div>

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent"></div>
            </div>
          )}

          {/* Collapsible Filter Panel */}
          {showFilters && (
            <div className={`mb-6 rounded-xl border-2 overflow-hidden transition-all duration-300 ${
              darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50/80 border-gray-200'
            }`}>
              {/* Saved Presets */}
              {filterPresets.length > 0 && (
                <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Saved Presets</p>
                  <div className="flex flex-wrap gap-2">
                    {filterPresets.map((preset, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        <button onClick={() => loadFilterPreset(preset)}
                          className={`px-2.5 py-1 text-xs rounded-full transition-colors ${darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                        >{preset.name}</button>
                        <button onClick={() => deleteFilterPreset(idx)}
                          className={`p-0.5 rounded-full ${darkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                        ><FaTimes className="w-2.5 h-2.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Filter History */}
              {filterHistory.length > 0 && (
                <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Recent Filters</p>
                  <div className="flex flex-wrap gap-2">
                    {filterHistory.slice(0, 3).map((entry, idx) => (
                      <button key={idx} onClick={() => { setFilters(entry.filters); setQuickSearch(entry.quickSearch || ''); }}
                        className={`px-2.5 py-1 text-xs rounded-full transition-colors ${darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {entry.quickSearch ? `"${entry.quickSearch}"` : entry.filters.status || 'Custom'} — {new Date(entry.timestamp).toLocaleDateString()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Basic Filters Grid */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Search Type */}
                  <div>
                    <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Search In</label>
                    <Select styles={selectStyles}
                      value={filters.searchType ? { value: filters.searchType, label: { all: 'All Fields', orderNumber: 'Order Number', recipient: 'Recipient', contact: 'Contact Person', status: 'Status' }[filters.searchType] } : null}
                      onChange={(opt) => handleFilterChange('searchType', opt?.value || 'all')}
                      options={[
                        { value: 'all', label: 'All Fields' },
                        { value: 'orderNumber', label: 'Order Number' },
                        { value: 'recipient', label: 'Recipient' },
                        { value: 'contact', label: 'Contact Person' },
                        { value: 'status', label: 'Status' }
                      ]}
                      placeholder="Search in..."
                      isClearable
                    />
                  </div>
                  {/* Status */}
                  <div>
                    <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</label>
                    <Select styles={selectStyles}
                      value={filters.status ? { value: filters.status, label: filters.status } : null}
                      onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                      options={[
                        { value: 'Pending', label: 'Pending' },
                        { value: 'Partial', label: 'Partial' },
                        { value: 'Fulfilled', label: 'Fulfilled' },
                        { value: 'Cancelled', label: 'Cancelled' }
                      ]}
                      placeholder="All Statuses"
                      isClearable
                    />
                  </div>
                  {/* Recipient Type */}
                  <div>
                    <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient Type</label>
                    <Select styles={selectStyles}
                      value={filters.recipientType ? { value: filters.recipientType, label: filters.recipientType } : null}
                      onChange={(opt) => handleFilterChange('recipientType', opt?.value || '')}
                      options={[
                        { value: 'Customer', label: 'Customer' },
                        { value: 'Dealer', label: 'Dealer' }
                      ]}
                      placeholder="All Types"
                      isClearable
                    />
                  </div>
                </div>

                {/* Date Range */}
                <div className={`p-3 rounded-xl border mb-4 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Date Range</label>
                    <Select styles={{
                      ...selectStyles,
                      container: (p) => ({ ...p, width: '140px' }),
                      control: (p, state) => ({ ...selectStyles.control(p, state), minHeight: '28px', height: '28px' }),
                      valueContainer: (p) => ({ ...p, padding: '0 6px', height: '28px' }),
                      input: (p) => ({ ...p, margin: 0, padding: 0 }),
                      indicatorsContainer: (p) => ({ ...p, height: '28px' })
                    }}
                      value={filters.dateRange ? { value: filters.dateRange, label: { today: 'Today', yesterday: 'Yesterday', thisWeek: 'This Week', lastWeek: 'Last Week', thisMonth: 'This Month', lastMonth: 'Last Month', last7Days: 'Last 7 Days', last30Days: 'Last 30 Days', thisYear: 'This Year' }[filters.dateRange] } : null}
                      onChange={(opt) => { if (opt) handleDateRangeChange(opt.value); else setFilters(prev => ({ ...prev, dateRange: '', startDate: null, endDate: null })); }}
                      options={[
                        { value: 'today', label: 'Today' }, { value: 'yesterday', label: 'Yesterday' },
                        { value: 'thisWeek', label: 'This Week' }, { value: 'lastWeek', label: 'Last Week' },
                        { value: 'thisMonth', label: 'This Month' }, { value: 'lastMonth', label: 'Last Month' },
                        { value: 'last7Days', label: 'Last 7 Days' }, { value: 'last30Days', label: 'Last 30 Days' },
                        { value: 'thisYear', label: 'This Year' }
                      ]}
                      placeholder="Quick range..."
                      isClearable
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <DatePicker
                        selected={filters.startDate}
                        onChange={(date) => setFilters(prev => ({ ...prev, startDate: date, dateRange: '' }))}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Start date"
                        className={`w-full px-3 py-2 text-sm rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500`}
                        isClearable
                      />
                    </div>
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>to</span>
                    <div className="flex-1">
                      <DatePicker
                        selected={filters.endDate}
                        onChange={(date) => setFilters(prev => ({ ...prev, endDate: date, dateRange: '' }))}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="End date"
                        className={`w-full px-3 py-2 text-sm rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500`}
                        isClearable
                      />
                    </div>
                    {(filters.startDate || filters.endDate) && (
                      <button onClick={() => setFilters(prev => ({ ...prev, startDate: null, endDate: null, dateRange: '' }))}
                        className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                      ><FaTimes className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>

                {/* Amount Range */}
                <div className={`p-3 rounded-xl border mb-4 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount Range</label>
                    <button onClick={() => setFilters(prev => ({ ...prev, minAmount: '100000', maxAmount: '' }))}
                      className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}
                    >High Value (1L+)</button>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="number" placeholder="Min ₹" value={filters.minAmount} onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500`}
                    />
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>to</span>
                    <input type="number" placeholder="Max ₹" value={filters.maxAmount} onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500`}
                    />
                  </div>
                </div>

                {/* Advanced Filters Toggle */}
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center gap-2 text-xs font-medium mb-3 ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
                >
                  <FaFilter className="w-3 h-3" />
                  {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                </button>

                {/* Advanced Filters Panel */}
                {showAdvancedFilters && (
                  <div className={`p-3 rounded-xl border mb-4 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Salesperson */}
                      <div>
                        <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Salesperson</label>
                        <Select styles={selectStyles} isMulti
                          value={(employees || []).filter(e => filters.salesmen.includes(e._id)).map(e => ({ value: e._id, label: e.name }))}
                          onChange={(opts) => handleMultiSelectFilter('salesmen', opts)}
                          options={(employees || []).map(e => ({ value: e._id, label: e.name }))}
                          placeholder="Select salesperson..."
                        />
                      </div>
                      {/* Created By */}
                      <div>
                        <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By</label>
                        <Select styles={selectStyles} isMulti
                          value={(employees || []).filter(e => filters.createdBy.includes(e._id)).map(e => ({ value: e._id, label: e.name }))}
                          onChange={(opts) => handleMultiSelectFilter('createdBy', opts)}
                          options={(employees || []).map(e => ({ value: e._id, label: e.name }))}
                          placeholder="Select creator..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons Row */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <button onClick={() => { saveFilterPreset(); addToFilterHistory(); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                    ><FaSave className="w-3 h-3" /> Save Preset</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={resetAllFilters}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${darkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                    >Reset All</button>
                    <button onClick={() => { addToFilterHistory(); setShowFilters(false); }}
                      className={`px-4 py-1.5 text-xs font-medium rounded-lg text-white transition-colors bg-indigo-600 hover:bg-indigo-700`}
                    >Apply & Close</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 7-Day Statistics Cards */}
          {lastSevenDaysStats && !loading && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className={`p-3 rounded-xl border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <FaChartBar className={`w-3 h-3 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                  <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Recent Orders</span>
                </div>
                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{lastSevenDaysStats.totalOrders}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Last 7 days</p>
              </div>
              <div className={`p-3 rounded-xl border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <FaChartBar className={`w-3 h-3 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                  <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Revenue</span>
                </div>
                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>₹{lastSevenDaysStats.totalAmount}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Last 7 days</p>
              </div>
              <div className={`p-3 rounded-xl border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <FaChartBar className={`w-3 h-3 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                  <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Pending</span>
                    <span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{lastSevenDaysStats.pendingOrders}</span>
                  </div>
                  <div className={`w-full h-1 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-full rounded-full bg-yellow-500" style={{ width: `${lastSevenDaysStats.totalOrders ? (lastSevenDaysStats.pendingOrders / lastSevenDaysStats.totalOrders * 100) : 0}%` }}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Partial</span>
                    <span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{lastSevenDaysStats.partialOrders}</span>
                  </div>
                  <div className={`w-full h-1 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-full rounded-full bg-blue-500" style={{ width: `${lastSevenDaysStats.totalOrders ? (lastSevenDaysStats.partialOrders / lastSevenDaysStats.totalOrders * 100) : 0}%` }}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Fulfilled</span>
                    <span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{lastSevenDaysStats.fulfilledOrders}</span>
                  </div>
                  <div className={`w-full h-1 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-full rounded-full bg-green-500" style={{ width: `${lastSevenDaysStats.totalOrders ? (lastSevenDaysStats.fulfilledOrders / lastSevenDaysStats.totalOrders * 100) : 0}%` }}></div>
                  </div>
                </div>
              </div>
              <div className={`p-3 rounded-xl border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <FaChartBar className={`w-3 h-3 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Amount by Status</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between"><span className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Pending</span><span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>₹{lastSevenDaysStats.pendingAmount}</span></div>
                  <div className="flex items-center justify-between"><span className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Partial</span><span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>₹{lastSevenDaysStats.partialAmount}</span></div>
                  <div className="flex items-center justify-between"><span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Fulfilled</span><span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>₹{lastSevenDaysStats.fulfilledAmount}</span></div>
                  <div className="flex items-center justify-between"><span className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Cancelled</span><span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>₹{lastSevenDaysStats.cancelledAmount}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Search Results Summary */}
          <div className={`flex items-center justify-between mb-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span>
              Showing {filteredOrders.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage + 1)}-{Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} filtered orders
              {filteredOrders.length !== (orders?.length || 0) && ` (from ${orders?.length || 0} loaded)`}
            </span>
            {count > 0 && <span>Server: page {page} of {totalPages} ({count} total)</span>}
          </div>

          {/* Orders Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className={`text-center py-16 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <FaClipboardList className="w-10 h-10 opacity-40" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No Sales Orders Found</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {quickSearch || filters.status ? 'Try adjusting your search or filters' : 'Create your first sales order above to get started'}
              </p>
              {(quickSearch || filters.status || filters.startDate) && (
                <button onClick={resetAllFilters} className="px-4 py-2 text-xs bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors">
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}">
              <table className={`w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <thead>
                  <tr className={darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-750' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}>
                    <th className={`px-4 py-3.5 text-left font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Order #</th>
                    <th className={`px-4 py-3.5 text-left font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Date</th>
                    <th className={`px-4 py-3.5 text-left font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Recipient</th>
                    <th className={`px-4 py-3.5 text-left font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Items</th>
                    <th className={`px-4 py-3.5 text-right font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Amount</th>
                    <th className={`px-4 py-3.5 text-center font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Status</th>
                    <th className={`px-4 py-3.5 text-center font-bold text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-white'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((order, idx) => (
                    <tr key={order._id} className={`border-t transition-all duration-200 ${
                      darkMode ? 'border-gray-700 hover:bg-gray-750' : `border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-indigo-50/30`
                    }`}>
                      <td className="px-4 py-3.5 font-bold text-blue-600">{order.orderNumber}</td>
                      <td className="px-4 py-3.5">
                        {new Date(order.date || order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="font-semibold">{order.recipient?.name || order.recipient?.businessName || 'N/A'}</div>
                        <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{order.recipientType}</div>
                        {order.recipientType === 'Dealer' && order.dealerSalesmen?.length > 0 && (
                          <div className={`text-xs mt-0.5 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            {order.dealerSalesmen.map(s => s.name || s).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-1">
                          {order.items?.length > 0 && (
                            <span className={`px-1.5 py-0.5 text-xs rounded ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                              {order.items.length} tile{order.items.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {order.stockItems?.length > 0 && (
                            <span className={`px-1.5 py-0.5 text-xs rounded ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                              {order.stockItems.length} stock
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right font-bold">₹{formatNumberWithCommas(order.totalAmount || 0)}</td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={getStatusBadge(order.status)}>{order.status}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => handleViewOrder(order)} title="View"
                            className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-50'}`}>
                            <FaEye className="w-4 h-4" />
                          </button>
                          {order.status !== 'Fulfilled' && order.status !== 'Cancelled' && (
                            <button onClick={() => handleEdit(order)} title="Edit"
                              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-50'}`}>
                              <FaEdit className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => handleDuplicateOrder(order)} title="Duplicate Order"
                            className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-indigo-400 hover:bg-indigo-900/30' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                            <FaCopy className="w-4 h-4" />
                          </button>
                          {order.status !== 'Fulfilled' && order.status !== 'Cancelled' && (
                            <button onClick={() => handleCreateDO(order)} title="Create Delivery Order"
                              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-yellow-400 hover:bg-yellow-900/30' : 'text-yellow-600 hover:bg-yellow-50'}`}>
                              <FaTruck className="w-4 h-4" />
                            </button>
                          )}
                          {order.status === 'Pending' && (
                            <button onClick={() => handleCancelOrder(order)} title="Cancel Order"
                              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}`}>
                              <FaBan className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Client-Side Pagination */}
          {filteredOrders.length > itemsPerPage && (
            <div className={`flex items-center justify-center gap-1 mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className={`px-2 py-1 text-xs rounded-lg transition-colors ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >First</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className={`px-2 py-1 text-xs rounded-lg transition-colors ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              ><FaChevronLeft className="w-3 h-3" /></button>
              {(() => {
                const clientTotalPages = Math.ceil(filteredOrders.length / itemsPerPage);
                const pages = [];
                let startP = Math.max(1, currentPage - 2);
                let endP = Math.min(clientTotalPages, startP + 4);
                if (endP - startP < 4) startP = Math.max(1, endP - 4);
                for (let i = startP; i <= endP; i++) pages.push(i);
                return pages.map(p => (
                  <button key={p} onClick={() => setCurrentPage(p)}
                    className={`w-8 h-8 text-xs rounded-lg transition-colors ${
                      p === currentPage
                        ? 'bg-indigo-600 text-white shadow-md' 
                        : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >{p}</button>
                ));
              })()}
              <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredOrders.length / itemsPerPage), p + 1))} disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                className={`px-2 py-1 text-xs rounded-lg transition-colors ${currentPage >= Math.ceil(filteredOrders.length / itemsPerPage) ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              ><FaChevronRight className="w-3 h-3" /></button>
              <button onClick={() => setCurrentPage(Math.ceil(filteredOrders.length / itemsPerPage))} disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                className={`px-2 py-1 text-xs rounded-lg transition-colors ${currentPage >= Math.ceil(filteredOrders.length / itemsPerPage) ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >Last</button>
            </div>
          )}

          {/* Server-Side Pagination (Load More Data) */}
          {totalPages > 1 && (
            <div className={`flex items-center justify-between mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Server Page {page} of {totalPages} ({count} total orders)
              </span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all duration-200 ${
                    page <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
                  } ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-indigo-300'}`}
                ><FaChevronLeft className="w-3 h-3" /> Previous</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all duration-200 ${
                    page >= totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
                  } ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-indigo-300'}`}
                >Next <FaChevronRight className="w-3 h-3" /></button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* ===== RECIPIENT SELECTION MODAL ===== */}
      <MasterSelection
        isOpen={showRecipientModal}
        onClose={() => setShowRecipientModal(false)}
        onSelect={handleRecipientSelect}
        allowedTypes={['Customer', 'Dealer']}
        defaultType={form.recipientType}
        title="Select Recipient"
      />

      {/* ===== TILES SELECTION MODAL (Shared Component) ===== */}
      <TileSelectionModal
        isOpen={showTilesModal}
        onClose={() => setShowTilesModal(false)}
        onSelect={handleTileSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
        locationId={null}
        showNetStock={true}
      />

      {/* ===== STOCK ITEMS SELECTION MODAL (Shared Component) ===== */}
      <StockItemSelectionModal
        isOpen={showStockItemsModal}
        onClose={() => setShowStockItemsModal(false)}
        onSelect={handleStockItemSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
        locationId={null}
        showNetStock={true}
      />

      {/* ===== CONFIRMATION MODAL ===== */}
      {showConfirmationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className={`rounded-2xl shadow-2xl w-full max-w-lg m-4 transition-all duration-300 border-2 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`p-6 border-b-2 rounded-t-2xl ${
              darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <FaClipboardList className="text-white text-lg" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Confirm {isEditing ? 'Update' : 'Creation'}
                  </h3>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Review order details before submitting</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient</span>
                  <span className={`font-semibold text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{form.recipient?.label || 'N/A'}</span>
                </div>
                <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tile Items</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>{form.items?.filter(i => i.tileId || i.Name).length || 0}</span>
                </div>
                <div className={`flex justify-between items-center p-3 rounded-xl ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Stock Items</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>{form.stockItems?.filter(i => i.stockItemId || i.name).length || 0}</span>
                </div>
                <div className={`flex justify-between items-center p-4 mt-2 rounded-xl border-2 ${darkMode ? 'border-green-800 bg-green-900/20' : 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50'}`}>
                  <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Total Amount</span>
                  <span className="font-bold text-lg text-green-600">₹{formatNumberWithCommas(form.totalAmount || 0)}</span>
                </div>
              </div>
            </div>
            <div className={`flex gap-3 p-6 border-t-2 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <button
                onClick={() => setShowConfirmationModal(false)}
                className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                ref={confirmButtonRef}
                onClick={confirmSubmit}
                disabled={submitting}
                className="flex-1 py-3 rounded-xl font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
              >
                {submitting ? 'Submitting...' : isEditing ? 'Update Order' : 'Create Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== VIEW ORDER MODAL ===== */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className={`rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto m-4 transition-all duration-300 border-2 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`flex items-center justify-between p-5 sm:p-6 border-b-2 rounded-t-2xl ${
              darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50'
            }`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FaClipboardList className="text-white text-xl" />
                </div>
                <div>
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Sales Order: <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{selectedOrder.orderNumber}</span>
                  </h3>
                  <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Created on {new Date(selectedOrder.createdAt).toLocaleDateString('en-IN')} by {selectedOrder.createdBy?.name || 'Unknown'}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowViewModal(false)} className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 sm:p-6">
              {/* Order Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className={`p-5 rounded-xl border-2 ${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'}`}>
                  <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                    <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <FaClipboardList className="text-white text-xs" />
                    </div>
                    Order Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Status:</span>
                      <span className={getStatusBadge(selectedOrder.status)}>{selectedOrder.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Date:</span>
                      <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{new Date(selectedOrder.date || selectedOrder.createdAt).toLocaleDateString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Total Amount:</span>
                      <span className="font-bold text-green-600">₹{formatNumberWithCommas(selectedOrder.totalAmount || 0)}</span>
                    </div>
                    {selectedOrder.salesman?.length > 0 && (
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Salesperson:</span>
                        <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedOrder.salesman.map(s => s.name || s).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`p-5 rounded-xl border-2 ${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-100'}`}>
                  <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-800'}`}>
                    <div className="w-7 h-7 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                      <FaUser className="text-white text-xs" />
                    </div>
                    Recipient Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Type:</span>
                      <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedOrder.recipientType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Name:</span>
                      <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedOrder.recipient?.name || selectedOrder.recipient?.businessName || 'N/A'}</span>
                    </div>
                    {selectedOrder.contactPersonName && (
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Contact:</span>
                        <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedOrder.contactPersonName}</span>
                      </div>
                    )}
                    {selectedOrder.contactNumber && (
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Phone:</span>
                        <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedOrder.contactNumber}</span>
                      </div>
                    )}
                    {selectedOrder.siteAddress && (
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Site:</span>
                        <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedOrder.siteAddress}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tiles Items Table */}
              {selectedOrder.items?.length > 0 && (
                <div className="mb-6">
                  <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                    <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <FaCubes className="text-white text-xs" />
                    </div>
                    Tiles Items ({selectedOrder.items.length})
                  </h4>
                  <div className="overflow-x-auto rounded-xl border-2 border-gray-200 dark:border-gray-600">
                    <table className={`w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <thead>
                        <tr className={darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-650' : 'bg-gradient-to-r from-gray-50 to-gray-100'}>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">#</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Tile</th>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">Fresh B/P</th>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">Corner Dmg B/P</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">Rate</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">Disc%</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">Delivered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, idx) => (
                          <tr key={idx} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <td className="px-3 py-2">{idx + 1}</td>
                            <td className="px-3 py-2 font-medium">{item.tileName || (typeof item.tileId === 'object' ? item.tileId?.tilesName : '') || 'N/A'}</td>
                            <td className="px-3 py-2 text-center">{item.freshBoxes || 0}/{item.freshPieces || 0}</td>
                            <td className="px-3 py-2 text-center">{item.damagedBoxes || 0}/{item.damagedPieces || 0}</td>
                            <td className="px-3 py-2 text-right">₹{formatNumberWithCommas(item.rate || 0)}</td>
                            <td className="px-3 py-2 text-right">{item.discount || 0}%</td>
                            <td className="px-3 py-2 text-right font-medium">₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-xs ${
                                (item.deliveredFreshBoxes || 0) + (item.deliveredFreshPieces || 0) > 0
                                  ? 'text-green-600' : (darkMode ? 'text-gray-500' : 'text-gray-400')
                              }`}>
                                {item.deliveredFreshBoxes || 0}/{item.deliveredFreshPieces || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Stock Items Table */}
              {selectedOrder.stockItems?.length > 0 && (
                <div className="mb-6">
                  <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-purple-400' : 'text-purple-800'}`}>
                    <div className="w-7 h-7 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                      <FaBoxes className="text-white text-xs" />
                    </div>
                    Stock Items ({selectedOrder.stockItems.length})
                  </h4>
                  <div className="overflow-x-auto rounded-xl border-2 border-gray-200 dark:border-gray-600">
                    <table className={`w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <thead>
                        <tr className={darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-650' : 'bg-gradient-to-r from-gray-50 to-gray-100'}>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">#</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Item</th>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">Fresh Qty</th>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">Dmg Qty</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">Rate</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">Disc%</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">Delivered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.stockItems.map((si, idx) => (
                          <tr key={idx} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <td className="px-3 py-2">{idx + 1}</td>
                            <td className="px-3 py-2 font-medium">{si.name || (typeof si.stockItemId === 'object' ? si.stockItemId?.name : '') || 'N/A'}</td>
                            <td className="px-3 py-2 text-center">{si.freshQuantity || 0}</td>
                            <td className="px-3 py-2 text-center">{si.damagedQuantity || 0}</td>
                            <td className="px-3 py-2 text-right">₹{formatNumberWithCommas(si.rate || 0)}</td>
                            <td className="px-3 py-2 text-right">{si.discount || 0}%</td>
                            <td className="px-3 py-2 text-right font-medium">₹{formatNumberWithCommas(si.amountAfterDiscount || 0)}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-xs ${
                                (si.deliveredFreshQuantity || 0) > 0 ? 'text-green-600' : (darkMode ? 'text-gray-500' : 'text-gray-400')
                              }`}>
                                {si.deliveredFreshQuantity || 0}/{si.deliveredDamagedQuantity || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Linked Delivery Orders */}
              {selectedOrder.deliveryOrders?.length > 0 && (
                <div className="mb-6">
                  <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-800'}`}>
                    <div className="w-7 h-7 bg-gradient-to-r from-yellow-500 to-amber-600 rounded-lg flex items-center justify-center">
                      <FaFileAlt className="text-white text-xs" />
                    </div>
                    Linked Delivery Orders ({selectedOrder.deliveryOrders.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedOrder.deliveryOrders.map((doRef, idx) => (
                      <div key={idx} className={`p-4 rounded-xl border-2 flex items-center justify-between ${
                        darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-100'
                      }`}>
                        <span className="font-medium">{doRef.deliveryOrderId?.doNumber || doRef.deliveryOrderId || 'N/A'}</span>
                        <span className={getStatusBadge(doRef.status || doRef.deliveryOrderId?.status || 'N/A')}>
                          {doRef.status || doRef.deliveryOrderId?.status || 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Narration */}
              {selectedOrder.narration && (
                <div className={`p-5 rounded-xl border-2 ${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'}`}>
                  <h4 className={`font-bold mb-2 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="w-7 h-7 bg-gradient-to-r from-gray-500 to-slate-600 rounded-lg flex items-center justify-center">
                      <FaFileAlt className="text-white text-xs" />
                    </div>
                    Narration
                  </h4>
                  <p className={`text-sm ml-9 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedOrder.narration}</p>
                </div>
              )}
            </div>

            <div className={`p-5 border-t-2 flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              {selectedOrder.status !== 'Fulfilled' && selectedOrder.status !== 'Cancelled' ? (
                <button onClick={() => { setShowViewModal(false); handleCreateDO(selectedOrder); }}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:from-yellow-600 hover:to-amber-700 transition-all duration-200 font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center gap-2">
                  <FaTruck className="w-4 h-4" /> Create Delivery Order
                </button>
              ) : <div />}
              <button onClick={() => setShowViewModal(false)}
                className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 font-bold shadow-lg hover:shadow-xl hover:scale-[1.02]">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== FLOATING ACTION BUTTON ===== */}
      {(form.recipient?.value && (form.items?.some(item => item.tileId || item.Name) || form.stockItems?.some(item => item.stockItemId || item.name))) && (
        <div className="fixed bottom-8 right-8 z-50">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="group relative w-16 h-16 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 hover:from-green-600 hover:via-emerald-600 hover:to-teal-700 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
          >
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-full"></div>

            {/* Icon */}
            <div className="relative z-10">
              {submitting ? (
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              ) : (
                <FaRocket className="w-8 h-8 group-hover:rotate-12 transition-transform duration-300" />
              )}
            </div>

            {/* Pulse Ring */}
            <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20"></div>

            {/* Hover Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              <div className="font-semibold mb-1">Quick Submit Order</div>
              <div className="text-gray-400 text-[10px]">Shortcut: Ctrl+Enter</div>
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default TilesSalesOrder;
